import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SessionLayout, DashboardView, SidebarView } from '../components/live-session';
import { AvatarContributionsTab } from '../components/AvatarContributionsTab';
import { TranscriptTab } from '../components/TranscriptTab';
import { AvatarCardsPanel } from '../components/AvatarCardsPanel';
import { SessionTasksTab } from '../components/session-tasks/SessionTasksTab';
import { SessionTaskDetailModal } from '../components/session-tasks/SessionTaskDetailModal';
import { DevicesTab } from '../components/DevicesTab';
import ImagesTab from '../components/ImagesTab';
import WorkshopBookModal from '../components/WorkshopBookModal';
import sessionTasksService, { customerResearchService, GraphData, sessionService } from '../services/sessionTasksService';
import type { InspirationCard } from '../types/inspiration-wall.types';
import { useProject } from '../hooks/useProjects';
import { BackgroundInfo } from '../components/BackgroundModal';
import { Attendee } from '../components/AttendeesModal';
import SessionSetupView from '../components/live-session/SessionSetupView';
import { GuidanceModal, FacilitatorGuidance } from '../components/GuidanceModal';
import { FacilitatorGuidancePanel } from '../components/FacilitatorGuidancePanel';
import { RequirementsPanel } from '../components/RequirementsPanel';

import { GovernancePanel } from '../components/GovernancePanel';
import { SessionSummaryPanel } from '../components/SessionSummaryPanel';
import { ReportBuilderTab } from '../components/report-builder/ReportBuilderTab';
import { WhiteboardUploadModal } from '../components/WhiteboardUploadModal';
import { ShareSessionDialog } from '../components/ShareSessionDialog';
import SecondScreenConfigPanel from '../components/live-session/second-screen-config/SecondScreenConfigPanel';
import DecisionsTab from '../components/live-session/DecisionsTab';
import type { SessionDecision } from '../components/live-session/DecisionsTab';
import ActionsPanel from '../components/live-session/ActionsPanel';
import MeetingsTab from '../components/live-session/MeetingsTab';
import ExportPanel from '../components/live-session/ExportPanel';
import { MiroSyncPanel } from '../components/MiroSyncPanel';
import { ReachExtractTab } from '../components/live-session/ReachExtractTab';
import ShowcaseTab from '../components/session/ShowcaseTab';
import { WidgetLauncherPanel } from '../components/participant/WidgetLauncherPanel';
import { SessionGraphTab } from '../components/project/SessionGraphTab';
import { SessionAnalyticsPanel } from '../components/live-session/SessionAnalyticsPanel';
import { botChat, botSpeak, VexaPlatform } from '../services/vexa';
import { useAuth } from '../contexts/AuthContext';
import { endSession, reopenSession } from '../services/sessionEnd';
import { useSecondScreenBroadcast } from '../hooks/useSecondScreenBroadcast';
import { useMultiScreenWindows } from '../hooks/useSecondScreenWindow';
import { useAvatarMindBubbles } from '../hooks/useAvatarMindBubbles';
import { useCinematicFeed } from '../hooks/useCinematicFeed';
import type { PinnedContentItem, CinematicContribution } from '../types/second-screen.types';
import type { SessionTaskSummary } from '../types/session-task.types';
import { useHelp } from '../components/help/HelpContext';
import { toast } from 'sonner';
import { DeletionProgressModal } from '../components/common/DeletionProgressModal';

import { PHASES, TranscriptEntry, WhiteboardSource, FinalizedSentence, TemplateSnapshot } from '../types';
import {
  AvatarSuggestion,
  FacilitatorGuidance as FacilitatorGuidanceData,
  ProductRequirements,
  UserStory,
  ExtractedPersona
} from '../services/workshop';
import { getWhiteboardSources } from '../services/whiteboard';
import { useContinuousSpeechRecognition } from '../hooks/useContinuousSpeechRecognition';
import { api } from '../services/api';
import { callN8nWorkflow, approveAvatarContributionViaN8n, releaseSpeechViaN8n, listSessionDevicesViaN8n, DeviceInfo, stageAudioAsync, getStagedAudio, clearStagedAudio, stopSpeechViaN8n, muteMicViaN8n, unmuteMicViaN8n } from '../services/n8n';

// Normalize role identifiers — handles LLM-generated variants like "Devil_S_Advocate"
const normalizeRole = (role: string): string => {
  const lower = role.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/(?:^_|_$)/g, '');
  const aliases: Record<string, string> = {
    'devil_s_advocate': 'devils_advocate',
  };
  return aliases[lower] || lower;
};

export default function LiveSession() {
  const { id } = useParams<{ id: string }>();
  const sessionId = id || '';
  const navigate = useNavigate();
  const { hasFeature } = useAuth();

  // State
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [responseMode, setResponseMode] = useState('open_ideas');
  const [playbackMode, setPlaybackMode] = useState<'browser' | 'physical' | 'both'>('browser');
  const [userInput, setUserInput] = useState('');
  const [pendingContributions, setPendingContributions] = useState<AvatarSuggestion[]>([]);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [speakingAvatar, setSpeakingAvatar] = useState<string | null>(null);
  const [roomMicMuted, setRoomMicMuted] = useState(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [sessionAvatars, setSessionAvatars] = useState<any[]>([]);

  // Meeting bot connection state (lifted so it persists across tab switches + page refresh)
  const [meetingConnection, setMeetingConnection] = useState<{ platform: VexaPlatform; meetingId: string } | null>(() => {
    try {
      const saved = sessionStorage.getItem(`meeting_connection_${sessionId}`);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [meetingChatEnabled, setMeetingChatEnabled] = useState(() => {
    return sessionStorage.getItem(`meeting_chat_${sessionId}`) === 'true';
  });
  const [meetingSpeakEnabled, setMeetingSpeakEnabled] = useState(() => {
    return sessionStorage.getItem(`meeting_speak_${sessionId}`) === 'true';
  });
  const [meetingRelayAvatars, setMeetingRelayAvatars] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem(`meeting_relay_avatars_${sessionId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // Auto-mute room mic when avatar is speaking
  useEffect(() => {
    if (!sessionId) return;
    if (speakingAvatar) {
      muteMicViaN8n(sessionId).catch(err =>
        console.error('[Mic Mute] Failed to mute room mic:', err)
      );
      setRoomMicMuted(true);
    } else {
      unmuteMicViaN8n(sessionId).catch(err =>
        console.error('[Mic Mute] Failed to unmute room mic:', err)
      );
      setRoomMicMuted(false);
    }
  }, [speakingAvatar, sessionId]);

  const handleToggleRoomMic = useCallback(async () => {
    if (roomMicMuted) {
      await unmuteMicViaN8n(sessionId).catch(console.error);
      setRoomMicMuted(false);
    } else {
      await muteMicViaN8n(sessionId).catch(console.error);
      setRoomMicMuted(true);
    }
  }, [roomMicMuted, sessionId]);

  // Persist meeting connection to sessionStorage
  useEffect(() => {
    if (meetingConnection) {
      sessionStorage.setItem(`meeting_connection_${sessionId}`, JSON.stringify(meetingConnection));
    } else {
      sessionStorage.removeItem(`meeting_connection_${sessionId}`);
    }
  }, [meetingConnection, sessionId]);

  useEffect(() => {
    sessionStorage.setItem(`meeting_chat_${sessionId}`, String(meetingChatEnabled));
  }, [meetingChatEnabled, sessionId]);

  useEffect(() => {
    sessionStorage.setItem(`meeting_speak_${sessionId}`, String(meetingSpeakEnabled));
  }, [meetingSpeakEnabled, sessionId]);

  useEffect(() => {
    sessionStorage.setItem(`meeting_relay_avatars_${sessionId}`, JSON.stringify([...meetingRelayAvatars]));
  }, [meetingRelayAvatars, sessionId]);

  // Session persistence state
  const [sessionMode, setSessionMode] = useState<'active' | 'readOnly'>('active');
  const [sessionStatus, setSessionStatus] = useState<'active' | 'completed' | 'abandoned' | null>(null);
  const [userAccessLevel, setUserAccessLevel] = useState<'owner' | 'read' | 'write'>('owner');
  const [sessionInfo, setSessionInfo] = useState<{
    customerName?: string;
    lastActivityAt?: string;
    createdAt?: string;
  }>({});

  // Speech deduplication ref
  const lastSentTextRef = useRef<string>('');
  const lastSentTimeRef = useRef<number>(0);

  // Guard: prevent saving contributions before initial load completes
  const hasLoadedContributions = useRef(false);

  // Async avatar contribution tracking
  const [inFlightRequests, setInFlightRequests] = useState<{ requestId: string; startedAt: string }[]>([]);
  const knownContributionCount = useRef(0);
  const isAvatarsThinking = inFlightRequests.length > 0;

  // Facilitator guidance state
  const [facilitatorGuidance, setFacilitatorGuidance] = useState<FacilitatorGuidanceData | null>(null);
  const [guidanceLoading, setGuidanceLoading] = useState(false);

  // Modal states
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showWhiteboardModal, setShowWhiteboardModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showWorkshopBookModal, setShowWorkshopBookModal] = useState(false);


  // Modal data
  const [backgroundInfo, setBackgroundInfo] = useState<BackgroundInfo>(() => {
    try {
      const saved = localStorage.getItem(`backgroundInfo_${sessionId}`);
      return saved ? JSON.parse(saved) : { customerName: '', industry: '', context: '' };
    } catch { return { customerName: '', industry: '', context: '' }; }
  });
  const [sessionGoals, setSessionGoals] = useState<string[]>([]);
  const [attendees, setAttendees] = useState<Attendee[]>(() => {
    try {
      const saved = localStorage.getItem(`attendees_${sessionId}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [facilitators, setFacilitators] = useState<Attendee[]>(() => {
    try {
      const saved = localStorage.getItem(`facilitators_${sessionId}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [guidanceHistory, setGuidanceHistory] = useState<FacilitatorGuidance[]>([]);
  const [whiteboardSources, setWhiteboardSources] = useState<WhiteboardSource[]>([]);
  const [designContext, setDesignContext] = useState<string>(() => {
    try {
      return localStorage.getItem(`designContext_${sessionId}`) || '';
    } catch { return ''; }
  });
  const [imageStyle, setImageStyle] = useState<string>(() => {
    try {
      return localStorage.getItem(`imageStyle_${sessionId}`) || '';
    } catch { return ''; }
  });

  // Project association
  const [projectId, setProjectId] = useState<string | undefined>(undefined);

  // Persist setup data to localStorage
  useEffect(() => {
    localStorage.setItem(`backgroundInfo_${sessionId}`, JSON.stringify(backgroundInfo));
  }, [backgroundInfo, sessionId]);

  useEffect(() => {
    localStorage.setItem(`attendees_${sessionId}`, JSON.stringify(attendees));
  }, [attendees, sessionId]);

  useEffect(() => {
    localStorage.setItem(`facilitators_${sessionId}`, JSON.stringify(facilitators));
  }, [facilitators, sessionId]);

  // Persist attendees + facilitators to backend on change (debounced), and sync to graph
  const attendeesPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (attendeesPersistTimer.current) clearTimeout(attendeesPersistTimer.current);
    attendeesPersistTimer.current = setTimeout(() => {
      sessionService.updateAttendees(sessionId, attendees, facilitators).catch(() => {});
      if (projectId) {
        customerResearchService.syncParticipants(projectId, sessionId, attendees, facilitators)
          .then(() => new Promise(r => setTimeout(r, 2000)))
          .then(() => customerResearchService.getGraphData(projectId))
          .then(setGraphData)
          .catch(() => {});
      }
    }, 800);
    return () => { if (attendeesPersistTimer.current) clearTimeout(attendeesPersistTimer.current); };
  }, [attendees, facilitators, sessionId, projectId]);

  useEffect(() => {
    localStorage.setItem(`designContext_${sessionId}`, designContext);
  }, [designContext, sessionId]);

  const designContextPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (designContextPersistTimer.current) clearTimeout(designContextPersistTimer.current);
    designContextPersistTimer.current = setTimeout(() => {
      sessionService.updateDesignContext(sessionId, designContext).catch(() => {});
    }, 1000);
    return () => { if (designContextPersistTimer.current) clearTimeout(designContextPersistTimer.current); };
  }, [designContext, sessionId]);

  useEffect(() => {
    localStorage.setItem(`imageStyle_${sessionId}`, imageStyle);
  }, [imageStyle, sessionId]);

  const imageStylePersistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (imageStylePersistTimer.current) clearTimeout(imageStylePersistTimer.current);
    imageStylePersistTimer.current = setTimeout(() => {
      sessionService.updateImageStyle(sessionId, imageStyle).catch(() => {});
    }, 1000);
    return () => { if (imageStylePersistTimer.current) clearTimeout(imageStylePersistTimer.current); };
  }, [imageStyle, sessionId]);

  // Persist active sidebar view in URL search params so it survives page refresh
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = (searchParams.get('tab') as SidebarView) || 'dashboard';
  const setActiveView = useCallback((view: SidebarView) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (view === 'dashboard') {
        next.delete('tab');
      } else {
        next.set('tab', view);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Sync active view to help context
  const { setActiveHelpView } = useHelp();
  useEffect(() => {
    setActiveHelpView(activeView);
    return () => setActiveHelpView(null);
  }, [activeView, setActiveHelpView]);

  // Auto-respond toggle state
  const [autoRespondEnabled, setAutoRespondEnabled] = useState<boolean>(false);
  const [loadingAutoRespondState, setLoadingAutoRespondState] = useState<boolean>(true);

  // Avatar response settings
  const [maxResponseWords, setMaxResponseWords] = useState<number>(100);
  const [intentDetectEnabled, setIntentDetectEnabled] = useState<boolean>(true);
  const [avatarBubblesEnabled, setAvatarBubblesEnabled] = useState<boolean>(true);
  const [ttsProvider, setTtsProvider] = useState<'off' | 'elevenlabs'>('elevenlabs');
  const [converseProvider, setConverseProvider] = useState<'off' | 'elevenlabs'>('elevenlabs');

  // Showcase state
  const [showcaseEnabled, setShowcaseEnabled] = useState<boolean>(false);
  const [showcaseId, setShowcaseId] = useState<string | undefined>(undefined);
  const [showcaseLinks, setShowcaseLinks] = useState<any[]>([]);

  // Audio processing state
  const [processingAudio, setProcessingAudio] = useState<Set<number>>(new Set());

  const { data: projectData } = useProject(projectId || '');

  // Room binding
  const [sessionRoomId, setSessionRoomId] = useState<string | undefined>(undefined);
  const [sessionRoomName, setSessionRoomName] = useState<string | undefined>(undefined);
  const [sessionRoomLeaseExpiresAt, setSessionRoomLeaseExpiresAt] = useState<string | undefined>(undefined);

  // Second screen
  const { screen1, screen2, anyOpen: isSecondScreenOpen } = useMultiScreenWindows(sessionId);
  const openSecondScreen = screen1.open;
  const openThirdScreen = screen2.open;

  const activeRequirementsPersonaRef = useRef<string | null>(null);

  const handlePushRequirementsToScreen = useCallback(() => {
    const REQUIREMENTS_MIRROR_PRESET = JSON.stringify({
      id: 'requirements-mirror',
      name: 'Requirements Mirror',
      cols: 12,
      widgets: [{
        instanceId: 'push-requirements-mirror',
        widgetId: 'requirements-mirror',
        config: {},
        x: 0, y: 0, w: 12, h: 8,
      }],
    });
    // Target screen1 if open, screen2 if open, else open screen1
    if (screen1.isOpen) {
      localStorage.setItem(`second-screen-layout-${sessionId}-screen1`, REQUIREMENTS_MIRROR_PRESET);
    } else if (screen2.isOpen) {
      localStorage.setItem(`second-screen-layout-${sessionId}-screen2`, REQUIREMENTS_MIRROR_PRESET);
    } else {
      localStorage.setItem(`second-screen-layout-${sessionId}-screen1`, REQUIREMENTS_MIRROR_PRESET);
      screen1.open();
    }
  }, [sessionId, screen1, screen2]);

  // Inspiration Wall cards (populated from completed social-pulse tasks)
  const [inspirationCards, setInspirationCards] = useState<InspirationCard[]>([]);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [sessionTasksForScreen, setSessionTasksForScreen] = useState<SessionTaskSummary[]>([]);
  const [pinnedContent, setPinnedContent] = useState<PinnedContentItem[]>([]);
  const [activeScreenTarget, setActiveScreenTarget] = useState<number | undefined>(undefined);
  const [doubleDiamondProgress, setDoubleDiamondProgress] = useState<import('../types/second-screen.types').DoubleDiamondProgress | null>(null);

  // Sync participants to knowledge graph (debounced)
  const participantSyncRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!projectId || (attendees.length === 0 && facilitators.length === 0)) return;

    if (participantSyncRef.current) clearTimeout(participantSyncRef.current);
    participantSyncRef.current = setTimeout(async () => {
      try {
        await customerResearchService.syncParticipants(projectId, sessionId, attendees, facilitators);
        setTimeout(async () => {
          try {
            const data = await customerResearchService.getGraphData(projectId);
            setGraphData(data);
          } catch { /* silent */ }
        }, 3000);
      } catch { /* silent — will retry on next change */ }
    }, 1000);

    return () => {
      if (participantSyncRef.current) clearTimeout(participantSyncRef.current);
    };
  }, [attendees, facilitators, projectId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server health check state (NEW: Session sync)
  const [serverStartTime, setServerStartTime] = useState<string | null>(null);
  const [sessionNeedsReload, setSessionNeedsReload] = useState<boolean>(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState<{ workflowId: string; executionId: string } | null>(null);

  // Governance state
  const [governanceData, setGovernanceData] = useState<{
    useCaseId: string;
    riskClassification: { level: number; label: string };
    surveys: Array<{
      surveyId: string;
      surveyName: string;
      questions: Array<{ questionId: string; question: string; answer: string }>;
    }>;
  } | null>(null);
  const [governanceLoading, setGovernanceLoading] = useState(false);
  const [expandedSurveys, setExpandedSurveys] = useState<Set<string>>(new Set());

  // Requirements state
  const [requirements, setRequirements] = useState<ProductRequirements | null>(() => {
    // Load from localStorage on mount
    try {
      const saved = localStorage.getItem(`requirements_${sessionId}`);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Failed to load requirements from localStorage:', error);
      return null;
    }
  });
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [removedRequirementIds, setRemovedRequirementIds] = useState<Set<string>>(() => {
    // Load from localStorage on mount
    try {
      const saved = localStorage.getItem(`removedRequirements_${sessionId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (error) {
      console.error('Failed to load removed requirements from localStorage:', error);
      return new Set();
    }
  });
  const [removedPersonaIds, setRemovedPersonaIds] = useState<Set<string>>(() => {
    // Load from localStorage on mount
    try {
      const saved = localStorage.getItem(`removedPersonas_${sessionId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (error) {
      console.error('Failed to load removed personas from localStorage:', error);
      return new Set();
    }
  });

  // Decisions state
  const [decisions, setDecisions] = useState<SessionDecision[]>([]);
  const [decisionsLoading, setDecisionsLoading] = useState(false);
  const [decisionsScanning, setDecisionsScanning] = useState(false);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  const transcriptRef = useRef<HTMLDivElement>(null);
  const loadRequirementsIntervalRef = useRef<(() => void) | null>(null);

  // Template snapshot state (for template-based sessions)
  const [templateSnapshot, setTemplateSnapshot] = useState<TemplateSnapshot | null>(null);

  // Normalize template phases to match WorkshopPhase interface
  const normalizePhase = (phase: TemplateSnapshot['phases'][number]): any => {
    return {
      id: phase.phaseTemplateId || phase.name.toLowerCase().replace(/\s+/g, '_'),
      name: phase.name,
      duration: phase.duration,
      objectives: phase.purpose, // Map 'purpose' to 'objectives' for compatibility
      goal: phase.goal,
      successCriteria: phase.successCriteria
    };
  };

  // Determine current phase - use template phases if available, otherwise hardcoded PHASES
  const phases = templateSnapshot
    ? templateSnapshot.phases.map(normalizePhase)
    : PHASES;
  const currentPhase = phases[currentPhaseIndex];

  // Handle phase changes - persist to backend for template-based sessions
  const handlePhaseChange = async (newPhaseIndex: number) => {
    // Update local state immediately for responsive UI
    setCurrentPhaseIndex(newPhaseIndex);

    // Persist phase index to backend
    try {
      const nextPhase = phases[newPhaseIndex] || {};
      const phaseName = nextPhase.name || nextPhase.id || '';
      const phaseObjective = nextPhase.goal || nextPhase.objectives || '';
      await api.put(`/api/sessions/${sessionId}/phase-index`, {
        phaseIndex: newPhaseIndex,
        phaseName,
        phaseObjective,
      });
    } catch (error) {
      console.error('[Phase Change] Failed to persist phase change:', error);
      toast.error('Failed to save phase change');
    }
  };

  // Calculate similarity between two strings using simple word overlap
  const calculateSimilarity = (str1: string, str2: string): number => {
    if (!str1 || !str2) return 0;
    const words1 = str1.toLowerCase().split(/\s+/);
    const words2 = str2.toLowerCase().split(/\s+/);
    const set1 = new Set(words1);
    const set2 = new Set(words2);

    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);

    return intersection.size / union.size; // Jaccard similarity
  };

  // Find matching story from existing requirements
  const findMatchingStory = (newStory: UserStory, existingStories: UserStory[]): UserStory | null => {
    const newId = (newStory as any).storyId || newStory.id;
    const newOriginalId = (newStory as any).originalId;

    // Stable-ID match first — survives persona renames and re-extractions
    for (const existing of existingStories) {
      const exId = (existing as any).storyId || existing.id;
      const exOriginalId = (existing as any).originalId;
      if (newId && (newId === exId || newId === exOriginalId)) return existing;
      if (newOriginalId && (newOriginalId === exId || newOriginalId === exOriginalId)) return existing;
    }

    // Fall back to text similarity + persona match
    const SIMILARITY_THRESHOLD = 0.5;
    for (const existing of existingStories) {
      const similarity = calculateSimilarity(newStory.story, existing.story);
      if (similarity >= SIMILARITY_THRESHOLD && newStory.persona === existing.persona) {
        return existing;
      }
    }
    return null;
  };

  // Find matching persona from existing personas
  const findMatchingPersona = (newPersona: ExtractedPersona, existingPersonas: ExtractedPersona[]): ExtractedPersona | null => {
    const SIMILARITY_THRESHOLD = 0.5; // 50% word overlap

    for (const existing of existingPersonas) {
      // Match by name similarity and role
      const nameSimilarity = calculateSimilarity(newPersona.name, existing.name);
      const roleSimilarity = calculateSimilarity(newPersona.role, existing.role);

      if (nameSimilarity >= SIMILARITY_THRESHOLD ||
          (roleSimilarity >= SIMILARITY_THRESHOLD && newPersona.name.toLowerCase() === existing.name.toLowerCase())) {
        return existing;
      }
    }
    return null;
  };

  // Merge new requirements with existing ones
  const mergeRequirements = (
    existing: ProductRequirements | null,
    incoming: ProductRequirements
  ): ProductRequirements => {
    if (!existing) {
      // First time - mark all as new
      const now = new Date().toISOString();
      return {
        ...incoming,
        personas: incoming.personas.map(persona => ({
          ...persona,
          firstSeen: now,
          lastSeen: now,
          isNew: true,
          originalId: persona.id,
          id: `persona_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })),
        userStories: incoming.userStories.map(story => ({
          ...story,
          firstSeen: now,
          lastSeen: now,
          isNew: true,
          originalId: story.id,
          id: `story_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }))
      };
    }

    const now = new Date().toISOString();

    // Merge personas
    const mergedPersonas: ExtractedPersona[] = [...existing.personas];
    for (const incomingPersona of incoming.personas) {
      const match = findMatchingPersona(incomingPersona, mergedPersonas);

      if (match) {
        // Update existing persona
        const index = mergedPersonas.findIndex(p => p.id === match.id);
        mergedPersonas[index] = {
          ...match,
          ...incomingPersona,
          id: match.id, // Keep the stable ID
          firstSeen: match.firstSeen,
          lastSeen: now,
          isNew: false,
          originalId: incomingPersona.id
        };
      } else {
        // New persona - add it
        mergedPersonas.push({
          ...incomingPersona,
          firstSeen: now,
          lastSeen: now,
          isNew: true,
          originalId: incomingPersona.id,
          id: `persona_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      }
    }

    // Merge user stories
    const mergedStories: UserStory[] = [...existing.userStories];
    for (const incomingStory of incoming.userStories) {
      const match = findMatchingStory(incomingStory, mergedStories);

      if (match) {
        // Update existing story
        const index = mergedStories.findIndex(s => s.id === match.id);
        mergedStories[index] = {
          ...match,
          ...incomingStory,
          id: match.id, // Keep the stable ID
          firstSeen: match.firstSeen,
          lastSeen: now,
          isNew: false,
          originalId: incomingStory.id
        };
      } else {
        // New story - add it
        mergedStories.push({
          ...incomingStory,
          firstSeen: now,
          lastSeen: now,
          isNew: true,
          originalId: incomingStory.id,
          id: `story_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      }
    }

    // Mark personas and stories as not new after 5 minutes
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    mergedPersonas.forEach(persona => {
      if (persona.firstSeen && persona.firstSeen < fiveMinutesAgo) {
        persona.isNew = false;
      }
    });
    mergedStories.forEach(story => {
      if (story.firstSeen && story.firstSeen < fiveMinutesAgo) {
        story.isNew = false;
      }
    });

    return {
      ...incoming,
      personas: mergedPersonas,
      userStories: mergedStories
    };
  };

  // Load facilitator guidance
  const loadFacilitatorGuidance = useCallback(async () => {
    if (!sessionId) return;
    setGuidanceLoading(true);
    try {
      const { startFacilitatorGuidanceTask, getStoredFacilitatorGuidance } = await import('../services/n8n');
      await startFacilitatorGuidanceTask(
        sessionId,
        currentPhase.id,
        currentPhase.objectives,
        currentPhase.goal,
        currentPhase.successCriteria
      );
      toast.info('Generating guidance — this may take a moment');

      const startedAt = Date.now();
      const pollForGuidance = setInterval(async () => {
        try {
          const stored = await getStoredFacilitatorGuidance(sessionId);
          if (stored && new Date(stored.timestamp).getTime() > startedAt) {
            setFacilitatorGuidance(stored);
            setGuidanceLoading(false);
            clearInterval(pollForGuidance);
          }
        } catch { /* keep polling */ }
      }, 5000);

      setTimeout(() => {
        clearInterval(pollForGuidance);
        setGuidanceLoading(false);
      }, 180000);
    } catch (error) {
      console.error('Failed to start facilitator guidance:', error);
      toast.error('Failed to generate facilitator guidance');
      setGuidanceLoading(false);
    }
  }, [sessionId, currentPhase]);

  // Load requirements — fires async session task, result comes back via task callback
  const loadRequirements = async () => {
    if (!sessionId) return;

    setRequirementsLoading(true);
    try {
      const { sessionTasksService } = await import('../services/sessionTasksService');
      await sessionTasksService.createTask(sessionId, {
        taskTemplateId: 'session-task-requirements-extraction',
        taskName: 'Requirements Extraction',
        taskCategory: 'thinking',
        contextSnapshot: {
          customContext: {
            customerName: backgroundInfo.customerName || 'Workshop Customer',
            sessionId,
            projectId: projectId || ''
          }
        }
      });
      toast.info('Requirements extraction started — this may take a minute');
      const extractionStartedAt = Date.now();
      // Poll frequently until results arrive, then stop
      const pollForResults = setInterval(async () => {
        try {
          const { getStoredRequirements } = await import('../services/workshop');
          const stored = await getStoredRequirements(sessionId);
          // Only accept results that were generated AFTER this extraction started,
          // otherwise we'd stop on a stale blob from a previous run.
          const storedAt = stored?.generatedAt ? new Date(stored.generatedAt).getTime() : 0;
          if (stored && storedAt > extractionStartedAt) {
            setRequirements(prev => mergeRequirements(prev, stored));
            setRequirementsLoading(false);
            clearInterval(pollForResults);
          }
        } catch { /* keep polling */ }
      }, 5000);
      // Safety timeout: stop polling after 3 minutes
      setTimeout(() => {
        clearInterval(pollForResults);
        setRequirementsLoading(false);
      }, 180000);
    } catch (error) {
      console.error('Failed to trigger requirements extraction:', error);
      toast.error('Failed to trigger requirements extraction');
      setRequirementsLoading(false);
    }
  };

  // Lightweight reload — just GETs current DDB data, no new extraction task.
  // Used after per-persona story extraction so we don't trigger a wasteful re-run.
  const reloadRequirementsData = async () => {
    if (!sessionId) return;
    try {
      const { getStoredRequirements } = await import('../services/workshop');
      const stored = await getStoredRequirements(sessionId);
      if (stored) setRequirements(prev => mergeRequirements(prev, stored));
    } catch { /* non-fatal */ }
  };

  // Keep a ref to the latest loadRequirements function
  useEffect(() => {
    loadRequirementsIntervalRef.current = loadRequirements;
  });

  // Attempt to resume session on page load (NEW: Session persistence)
  useEffect(() => {
    const attemptResumeSession = async () => {
      if (!sessionId) return;

      try {

        // Try to resume from DynamoDB
        try {
          const resumeResponse = await api.get(`/api/sessions/${sessionId}/resume`);
          const resumeData = resumeResponse.data;


          setSessionStatus(resumeData.session.status);
          setSessionInfo({
            customerName: resumeData.session.customerName,
            lastActivityAt: resumeData.session.lastActivityAt,
            createdAt: resumeData.session.createdAt
          });

          // Set user access level from response
          if (resumeData.userAccessLevel) {
            setUserAccessLevel(resumeData.userAccessLevel);
          }

          if (resumeData.readOnly) {
            setSessionMode('readOnly');
          } else {
            setSessionMode('active');
          }

          // Hydrate removed IDs from DDB (merge with any already in localStorage)
          if (resumeData.session.removedStoryIds?.length) {
            setRemovedRequirementIds(prev => {
              const merged = new Set([...prev, ...resumeData.session.removedStoryIds]);
              try { localStorage.setItem(`removedRequirements_${sessionId}`, JSON.stringify([...merged])); } catch {}
              return merged;
            });
          }
          if (resumeData.session.removedPersonaIds?.length) {
            setRemovedPersonaIds(prev => {
              const merged = new Set([...prev, ...resumeData.session.removedPersonaIds]);
              try { localStorage.setItem(`removedPersonas_${sessionId}`, JSON.stringify([...merged])); } catch {}
              return merged;
            });
          }
        } catch (resumeError: any) {
          if (resumeError.response?.status === 403) {
            // Session exists but is not active (completed/abandoned)
            const data = resumeError.response.data;

            setSessionMode('readOnly');
            setSessionStatus(data.session?.status || 'completed');
            setSessionInfo({
              customerName: data.session?.customerName,
              lastActivityAt: data.session?.lastActivityAt,
              createdAt: data.session?.createdAt
            });

            // Set user access level if provided
            if (data.userAccessLevel) {
              setUserAccessLevel(data.userAccessLevel);
            }

            // Hydrate removed IDs for read-only sessions too
            if (data.session?.removedStoryIds?.length) {
              setRemovedRequirementIds(prev => {
                const merged = new Set([...prev, ...data.session.removedStoryIds]);
                try { localStorage.setItem(`removedRequirements_${sessionId}`, JSON.stringify([...merged])); } catch {}
                return merged;
              });
            }
            if (data.session?.removedPersonaIds?.length) {
              setRemovedPersonaIds(prev => {
                const merged = new Set([...prev, ...data.session.removedPersonaIds]);
                try { localStorage.setItem(`removedPersonas_${sessionId}`, JSON.stringify([...merged])); } catch {}
                return merged;
              });
            }
          } else if (resumeError.response?.status === 404) {
            // Session not found in DynamoDB, this is a new session
            setSessionMode('active');
            setSessionStatus('active');
          } else {
            throw resumeError; // Re-throw other errors
          }
        }

        // Load session avatars and governance data regardless of resume status
        const sessionResponse = await api.get(`/api/sessions/${sessionId}`);
        const sessionData = sessionResponse.data;

        // Store projectId for n8n workflow calls
        if (sessionData.projectId) {
          setProjectId(sessionData.projectId);
        }

        // Load room binding
        if (sessionData.roomId) {
          setSessionRoomId(sessionData.roomId);
          setSessionRoomName(sessionData.roomName);
          setSessionRoomLeaseExpiresAt(sessionData.roomLeaseExpiresAt);
        }

        // Hydrate attendees/facilitators from DynamoDB if available (overrides localStorage)
        if (sessionData.attendees?.length > 0) {
          setAttendees(sessionData.attendees);
        }
        if (sessionData.facilitators?.length > 0) {
          setFacilitators(sessionData.facilitators);
        }

        // Also get user access level from session data if available
        if (sessionData.userAccessLevel) {
          setUserAccessLevel(sessionData.userAccessLevel);
        }

        // Load template snapshot if this is a template-based session
        // Note: Backend spreads session fields at root level, so templateSnapshot is at sessionData.templateSnapshot
        if (sessionData.templateSnapshot) {
          setTemplateSnapshot(sessionData.templateSnapshot);
        }

        // Restore current phase index (works for both template-based and hardcoded phase sessions)
        if (typeof sessionData.currentPhaseIndex === 'number') {
          setCurrentPhaseIndex(sessionData.currentPhaseIndex);
        }

        if (sessionData.avatars && sessionData.avatars.length > 0) {
          setSessionAvatars(sessionData.avatars);
          // Avatars loaded
        } else {
          console.warn('[Avatar Colors] No avatars in session response!');
        }
        // Load governance data if it exists
        if (sessionData.governanceData) {
          setGovernanceData(sessionData.governanceData);
        }

        if (Array.isArray(sessionData.goals) && sessionData.goals.length > 0) {
          setSessionGoals(sessionData.goals);
        }

        if (sessionData.designContext) {
          setDesignContext(sessionData.designContext);
        }
        if (sessionData.imageStyle) {
          setImageStyle(sessionData.imageStyle);
        }
        if (sessionData.backgroundInfo) {
          setBackgroundInfo(sessionData.backgroundInfo);
        }

        // Load transcript from backend (NEW: Session persistence)
        try {
          const transcriptResponse = await api.get(`/api/sessions/${sessionId}/transcript?limit=100`);
          const transcriptData = transcriptResponse.data;

          if (transcriptData.transcript && transcriptData.transcript.length > 0) {
            setTranscript(transcriptData.transcript);
          }
        } catch (error) {
          console.error('[Session Resume] Failed to load transcript:', error);
          // Non-critical error - continue with empty transcript
        }

        // Load auto-respond toggle state (NEW: Manual control)
        try {
          const autoRespondResponse = await api.get(`/api/sessions/${sessionId}/auto-respond`);
          setAutoRespondEnabled(autoRespondResponse.data.autoRespondEnabled ?? true);
        } catch (error) {
          console.error('[Session Resume] Failed to load auto-respond state:', error);
          setAutoRespondEnabled(false); // Default to off on error
        } finally {
          setLoadingAutoRespondState(false);
        }

        // Load max response words setting
        try {
          const mwResponse = await api.get(`/api/sessions/${sessionId}/max-response-words`);
          setMaxResponseWords(mwResponse.data.maxResponseWords ?? 100);
        } catch (error) {
          console.error('[Session Resume] Failed to load max response words:', error);
        }

        // Load intent detect setting
        try {
          const idResponse = await api.get(`/api/sessions/${sessionId}/intent-detect`);
          setIntentDetectEnabled(idResponse.data.intentDetectEnabled ?? true);
        } catch (error) {
          console.error('[Session Resume] Failed to load intent detect setting:', error);
        }

        // Load avatar bubbles setting
        try {
          const abResponse = await api.get(`/api/sessions/${sessionId}/avatar-bubbles`);
          setAvatarBubblesEnabled(abResponse.data.avatarBubblesEnabled ?? true);
        } catch (error) {
          console.error('[Session Resume] Failed to load avatar bubbles setting:', error);
        }

        // Load provider settings
        try {
          const sessionRes = await api.get(`/api/sessions/${sessionId}`);
          const s = sessionRes.data?.session || sessionRes.data;
          if (s?.ttsProvider) setTtsProvider(s.ttsProvider);
          if (s?.converseProvider) setConverseProvider(s.converseProvider);
          if (s?.showcaseEnabled !== undefined) setShowcaseEnabled(s.showcaseEnabled);
          if (s?.showcaseId) setShowcaseId(s.showcaseId);
          if (s?.showcaseLinks) setShowcaseLinks(s.showcaseLinks);
        } catch (error) {
          console.error('[Session Resume] Failed to load provider settings:', error);
        }

        // Load pending contributions and reconnect any persisted staged audio
        try {
          const contributionsResponse = await api.get(`/api/sessions/${sessionId}/pending-contributions`);
          const contributions = contributionsResponse.data.pendingContributions || [];
          if (contributions.length > 0) {
            // Check for any ready staged audio to reconnect with contributions
            try {
              const stagedAudio = await getStagedAudio(sessionId);
              const reconnected = contributions.map((c: AvatarSuggestion) => {
                const staged = stagedAudio[c.role];
                if (!staged || staged.text !== c.response) return c;
                if (staged.status === 'ready' && (staged.audioUrl || staged.audioBase64)) {
                  return { ...c, audioUrl: staged.audioUrl || staged.audioBase64, audioS3Key: staged.s3Key || c.audioS3Key, audioGenerating: false };
                }
                if (staged.status === 'generating') {
                  return { ...c, audioGenerating: true };
                }
                return c;
              });
              setPendingContributions(reconnected);
            } catch (audioErr) {
              console.error('[Session Resume] Failed to load staged audio, using contributions without audio:', audioErr);
              setPendingContributions(contributions);
            }
          }
          knownContributionCount.current = contributions.length;
          hasLoadedContributions.current = true;
        } catch (error) {
          console.error('[Session Resume] Failed to load pending contributions:', error);
          hasLoadedContributions.current = true; // Allow saving even on error
        }

        // Load whiteboards from backend (NEW: Session persistence)
        try {
          const whiteboardResponse = await api.get(`/api/sessions/${sessionId}/whiteboards`);
          const whiteboardData = whiteboardResponse.data;

          if (whiteboardData.sources && whiteboardData.sources.length > 0) {
            // Transform backend format to frontend format
            const transformedSources = whiteboardData.sources.map((source: any) => ({
              sourceId: source.sourceId,
              sourceName: source.sourceName,
              lastUpload: source.timestamp  // Map timestamp to lastUpload
            }));
            setWhiteboardSources(transformedSources);
          }
        } catch (error) {
          console.error('[Session Resume] Failed to load whiteboards:', error);
          // Non-critical error - continue with empty whiteboards
        }

        // Facilitator guidance is loaded by the useEffect that watches currentPhaseIndex
        // Don't call loadFacilitatorGuidance() here — state updates (templateSnapshot, currentPhaseIndex)
        // haven't rendered yet, so it would use stale phase data (always "intros")
      } catch (error) {
        console.error('[Session Resume] Failed to resume session:', error);
        // Assume new session on error
        setSessionMode('active');
        setSessionStatus('active');
      }
    };

    attemptResumeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- session resume runs once per sessionId, not on callback changes
  }, [sessionId]);

  // Health check polling to detect backend restarts (NEW: Session sync)
  useEffect(() => {
    if (!sessionId) return;

    const checkHealth = async () => {
      try {
        const response = await api.get(`/api/sessions/health/${sessionId}`);
        const healthData = response.data;
        const { serverStartTime: currentServerStart, sessionInMemory } = healthData;

        // On first check, just store the server start time
        if (!serverStartTime) {
          setServerStartTime(currentServerStart);
          return;
        }

        // If server start time changed, backend restarted
        if (currentServerStart !== serverStartTime) {
          setServerStartTime(currentServerStart);
          setSessionNeedsReload(true);
        }

        // If session not in memory but server hasn't restarted, something's wrong
        if (!sessionInMemory && currentServerStart === serverStartTime) {
          console.warn('[Health Check] Session not in memory despite no restart detected');
          setSessionNeedsReload(true);
        }

        // Poll for new contributions from async avatar callbacks
        const serverContribCount = healthData.pendingContributionCount || 0;
        if (serverContribCount > knownContributionCount.current) {
          try {
            const contribResponse = await api.get(`/api/sessions/${sessionId}/pending-contributions`);
            const serverContributions = contribResponse.data.pendingContributions || [];
            setPendingContributions(serverContributions);
            knownContributionCount.current = serverContributions.length;
          } catch (err) {
            console.error('[Health Check] Failed to fetch new contributions:', err);
          }
        }

        // Poll for ready staged audio (async TTS via callback)
        const stagedAudioStatus = healthData.stagedAudioStatus || {};
        const readyRoles = Object.entries(stagedAudioStatus)
          .filter(([, entry]: [string, any]) => entry.status === 'ready')
          .map(([role]) => role);

        if (readyRoles.length > 0) {
          try {
            const stagedAudio = await getStagedAudio(sessionId);
            // Update contributions with the audio data
            setPendingContributions(prev =>
              prev.map(c => {
                const staged = stagedAudio[c.role];
                if (staged && staged.status === 'ready' && staged.text === c.response && (staged.audioUrl || staged.audioBase64)) {
                  return { ...c, audioUrl: staged.audioUrl || staged.audioBase64, audioS3Key: staged.s3Key || c.audioS3Key, audioGenerating: false };
                }
                return c;
              })
            );
            // Clear processing state for ready roles
            // Use setPendingContributions to access current state (avoids stale closure)
            setPendingContributions(current => {
              const indicesToClear = current
                .map((c, i) => readyRoles.includes(c.role) ? i : -1)
                .filter(i => i !== -1);
              if (indicesToClear.length > 0) {
                setProcessingAudio(prev => {
                  const newSet = new Set(prev);
                  indicesToClear.forEach(i => newSet.delete(i));
                  return newSet;
                });
              }
              return current; // no mutation
            });
            // Keep staged audio in backend so it survives page navigation
          } catch (err) {
            console.error('[Health Check] Failed to fetch staged audio:', err);
          }
        }

        // Timeout stale "generating" staged audio entries (>2 min) — clear so user can re-stage
        const staleGeneratingRoles = Object.entries(stagedAudioStatus)
          .filter(([, entry]: [string, any]) => entry.status === 'generating')
          .map(([role]) => role);
        if (staleGeneratingRoles.length > 0) {
          try {
            const stagedAudio = await getStagedAudio(sessionId);
            const staleThresholdMs = 120_000;
            for (const role of staleGeneratingRoles) {
              const entry = stagedAudio[role];
              if (entry?.startedAt && Date.now() - new Date(entry.startedAt).getTime() > staleThresholdMs) {
                console.warn(`[Health Check] Staged audio for ${role} stuck generating >2min, clearing`);
                clearStagedAudio(sessionId, role).catch(() => {});
                setPendingContributions(prev => {
                  const updated = prev.map(c => c.role === role ? { ...c, audioGenerating: false } : c);
                  // Clear processingAudio using current state
                  const indicesToClear = prev
                    .map((c, i) => c.role === role ? i : -1)
                    .filter(i => i !== -1);
                  if (indicesToClear.length > 0) {
                    setProcessingAudio(p => {
                      const newSet = new Set(p);
                      indicesToClear.forEach(i => newSet.delete(i));
                      return newSet;
                    });
                  }
                  return updated;
                });
              }
            }
          } catch (err) {
            console.error('[Health Check] Failed to check stale staged audio:', err);
          }
        }

        // Clear in-flight requests when all avatar requests are done
        const allDone = (healthData.avatarRequestsInFlight || 0) === 0;
        if (allDone) {
          setInFlightRequests(prev => prev.length === 0 ? prev : []);
        }

        // Phase Advisor — show toast when AI detects a phase change
        if (healthData.pendingPhaseAdvance) {
          const { suggestedPhaseIndex, phaseName, reasoning } = healthData.pendingPhaseAdvance;
          toast.info(`AI thinks workshop is now in "${phaseName}"`, {
            description: reasoning || undefined,
            duration: 30000,
            action: {
              label: 'Move to this phase',
              onClick: () => handlePhaseChange(suggestedPhaseIndex),
            },
          });
        }
      } catch (error) {
        console.error('[Health Check] Failed:', error);
        // Don't set sessionNeedsReload on network errors - only on state issues
      }
    };

    // Check immediately, then every 10 seconds
    checkHealth();
    const interval = setInterval(checkHealth, 10000);

    return () => clearInterval(interval);
  }, [sessionId, serverStartTime]);

  // Safety valve: clear in-flight requests older than 10s regardless of health check success.
  // The request is fire-and-forget — the backend accepts immediately — so buttons should not stay
  // locked if avatar_contributions_complete never arrives from n8n.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setInFlightRequests(prev => {
        if (prev.length === 0) return prev;
        const filtered = prev.filter(r => now - new Date(r.startedAt).getTime() < 10000);
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Auto-reload session when backend restart detected (NEW: Session sync)
  useEffect(() => {
    if (!sessionNeedsReload) return;

    const reloadSession = async () => {

      try {
        // Attempt to resume session from database
        await api.get(`/api/sessions/${sessionId}/resume`);

        // Reload transcript
        const transcriptResponse = await api.get(`/api/sessions/${sessionId}/transcript?limit=100`);
        if (transcriptResponse.data.transcript) {
          setTranscript(transcriptResponse.data.transcript);
        }

        // Reload session data
        const sessionResponse = await api.get(`/api/sessions/${sessionId}`);
        if (sessionResponse.data.avatars) {
          setSessionAvatars(sessionResponse.data.avatars);
        }

        // Reload auto-respond state
        const autoRespondResponse = await api.get(`/api/sessions/${sessionId}/auto-respond`);
        setAutoRespondEnabled(autoRespondResponse.data.autoRespondEnabled ?? true);

        setSessionNeedsReload(false);

        // Show notification to user
        toast.success('Session reconnected after server restart. All data has been restored.');
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 403 || status === 404) {
          setSessionNeedsReload(false);
          toast.info('Session has ended. Reload the page to return to the dashboard.');
          return;
        }
        console.error('[Session Sync] Failed to reload session:', error);
        setTimeout(reloadSession, 5000);
      }
    };

    reloadSession();
  }, [sessionNeedsReload, sessionId]);

  // Load stored requirements from backend on mount
  useEffect(() => {
    const loadStoredRequirements = async () => {
      if (!sessionId) return;

      try {
        const { getStoredRequirements } = await import('../services/workshop');
        const stored = await getStoredRequirements(sessionId);

        if (stored) {
          setRequirements({
            projectName: stored.projectName || 'Workshop Project',
            generatedAt: stored.generatedAt || new Date().toISOString(),
            sessionId: stored.sessionId || sessionId,
            personas: stored.personas || [],
            userStories: stored.userStories || [],
            nonFunctionalRequirements: stored.nonFunctionalRequirements || [],
            technicalSpecs: stored.technicalSpecs || {
              coreFunctionality: [],
              integrationPoints: [],
              dataRequirements: [],
              securityConsiderations: [],
              performanceTargets: []
            },
            constraints: stored.constraints || {
              technical: [],
              business: [],
              regulatory: []
            },
            assumptions: stored.assumptions || []
          });
          // Prune stale removed IDs — IDs that no longer exist in the loaded data are stale
          const validStoryIds = new Set((stored.userStories || []).map((s: any) => s.id));
          const validPersonaIds = new Set((stored.personas || []).map((p: any) => p.id));
          setRemovedRequirementIds(prev => {
            const pruned = new Set([...prev].filter(id => validStoryIds.has(id)));
            try { localStorage.setItem(`removedRequirements_${sessionId}`, JSON.stringify([...pruned])); } catch {}
            return pruned;
          });
          setRemovedPersonaIds(prev => {
            const pruned = new Set([...prev].filter(id => validPersonaIds.has(id)));
            try { localStorage.setItem(`removedPersonas_${sessionId}`, JSON.stringify([...pruned])); } catch {}
            return pruned;
          });
        }
      } catch (error) {
        console.error('[Requirements] Failed to load stored requirements:', error);
        toast.error('Failed to load stored requirements');
      }
    };

    loadStoredRequirements();
  }, [sessionId]);

  // Poll for updated requirements from backend (picks up async task results)
  useEffect(() => {
    const pollStoredRequirements = async () => {
      if (!sessionId) return;
      try {
        const { getStoredRequirements } = await import('../services/workshop');
        const stored = await getStoredRequirements(sessionId);
        if (stored) {
          setRequirements(prev => {
            const merged = mergeRequirements(prev, stored);
            return merged;
          });
        }
      } catch (e) { /* ignore */ }
    };

    const interval = setInterval(pollStoredRequirements, 60000); // Poll every 60s
    return () => clearInterval(interval);
  }, [sessionId]);

  // Decisions loading and polling
  const loadDecisions = useCallback(async () => {
    if (!sessionId) return;
    try {
      setDecisionsLoading(true);
      const res = await api.get(`/api/sessions/${sessionId}/decisions`);
      setDecisions(res.data.decisions || []);
    } catch (err) {
      console.error('Failed to load decisions:', err);
      toast.error('Failed to load decisions');
    } finally {
      setDecisionsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadDecisions();
    const interval = setInterval(loadDecisions, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [loadDecisions]);

  const handleAcceptDecision = useCallback(async (decisionId: string) => {
    try {
      await api.put(`/api/sessions/${sessionId}/decisions/${decisionId}`, { status: 'accepted' });
      await loadDecisions();
    } catch (err) {
      console.error('Failed to accept decision:', err);
      toast.error('Failed to accept decision');
    }
  }, [sessionId, loadDecisions]);

  const handleRejectDecision = useCallback(async (decisionId: string) => {
    try {
      await api.put(`/api/sessions/${sessionId}/decisions/${decisionId}`, { status: 'rejected' });
      await loadDecisions();
    } catch (err) {
      console.error('Failed to reject decision:', err);
      toast.error('Failed to reject decision');
    }
  }, [sessionId, loadDecisions]);

  const handleEditAcceptDecision = useCallback(async (decisionId: string, editedStatement: string, editedTopic: string) => {
    try {
      await api.put(`/api/sessions/${sessionId}/decisions/${decisionId}`, {
        status: 'accepted',
        editedStatement,
        editedTopic
      });
      await loadDecisions();
    } catch (err) {
      console.error('Failed to edit/accept decision:', err);
      toast.error('Failed to save decision edit');
    }
  }, [sessionId, loadDecisions]);

  const handleDecisionScan = useCallback(async () => {
    try {
      setDecisionsScanning(true);
      await api.post(`/api/sessions/${sessionId}/decisions/scan`, {});
      // Reload after a short delay to allow n8n processing
      setTimeout(loadDecisions, 5000);
    } catch (err) {
      console.error('Failed to trigger decision scan:', err);
      toast.error('Failed to trigger decision scan');
    } finally {
      setDecisionsScanning(false);
    }
  }, [sessionId, loadDecisions]);

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript]);

  // Auto-refresh transcript every 10s while transcript tab is active
  useEffect(() => {
    if (activeView !== 'transcript' || !sessionId) return;

    let cancelled = false;

    const refresh = async () => {
      if (document.hidden) return;
      try {
        const response = await api.get(`/api/sessions/${sessionId}/transcript?limit=100`);
        if (cancelled) return;
        const serverEntries: TranscriptEntry[] = response.data?.transcript || [];
        setTranscript(prev => {
          const serverIds = new Set(
            serverEntries.filter(e => e.id).map(e => e.id as string)
          );
          const localOnly = prev.filter(e => !e.id || !serverIds.has(e.id));
          return [...serverEntries, ...localOnly].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        });
      } catch (error) {
        console.error('[Transcript Refresh] Failed to refresh transcript:', error);
      }
    };

    const interval = setInterval(refresh, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeView, sessionId]);

  // Physical device state synchronization
  // Poll device states via HTTP every 10s
  const [sessionDevices, setSessionDevices] = useState<DeviceInfo[]>([]);

  const pollDevices = useCallback(async () => {
    if (!sessionId) return;
    try {
      const result = await listSessionDevicesViaN8n(sessionId);
      setSessionDevices(result.devices || []);

      // Update speaking avatar state from device states
      const speakingDevice = (result.devices || []).find(d => d.state === 'speaking');
      if (speakingDevice && speakingDevice.assignedRole) {
        setSpeakingAvatar(speakingDevice.assignedRole);
      } else {
        setSpeakingAvatar(null);
      }
    } catch (error) {
      // Silently handle — device API may not be running
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    const pollInterval = setInterval(pollDevices, 10000);

    return () => clearInterval(pollInterval);
  }, [sessionId, pollDevices]);

  // Poll for inspiration cards from completed social-pulse tasks
  useEffect(() => {
    if (!sessionId || !isSecondScreenOpen) return;

    const pollInspirationCards = async () => {
      try {
        const tasks = await sessionTasksService.listSessionTasks(sessionId, {
          status: ['completed'],
          category: ['doing']
        });
        const socialPulseTasks = tasks.filter(
          (t: any) => t.templateId === 'session-task-social-pulse'
        );
        if (socialPulseTasks.length === 0) return;

        // Get the most recent completed social-pulse task's outputs
        const latest = socialPulseTasks[0]; // Already sorted by createdAt desc
        const detail = await sessionTasksService.getTaskExecution(
          sessionId, latest.workflowId, latest.executionId || latest.workflowId
        );
        const cardOutput = (detail.outputs || []).find(
          (o: any) => o.type === 'inspiration_cards'
        );
        if (cardOutput?.content) {
          const cards = typeof cardOutput.content === 'string'
            ? JSON.parse(cardOutput.content)
            : cardOutput.content;
          if (Array.isArray(cards)) {
            // Only update if cards actually changed to avoid resetting the wall animation
            setInspirationCards(prev => {
              if (prev.length === cards.length && JSON.stringify(prev.map((c: any) => c.id)) === JSON.stringify(cards.map((c: any) => c.id))) {
                return prev;
              }
              return cards;
            });
          }
        }
      } catch {
        // Silently handle — task may not exist yet
      }
    };

    pollInspirationCards();
    const interval = setInterval(pollInspirationCards, 15000);
    return () => clearInterval(interval);
  }, [sessionId, isSecondScreenOpen]);

  // Poll for project graph data (for second screen and Knowledge tab)
  useEffect(() => {
    if (!projectId) return;

    const pollGraphData = async () => {
      try {
        const data = await customerResearchService.getGraphData(projectId);
        setGraphData(data);
      } catch {
        // Silently handle — graph may not be populated yet
      }
    };

    pollGraphData();
    const interval = setInterval(pollGraphData, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [projectId]);

  // Phase Advisor — trigger AI phase detection every 5 minutes
  useEffect(() => {
    if (!sessionId) return;
    const triggerPhaseAdvisor = async () => {
      try {
        await api.post(`/api/sessions/${sessionId}/phase-advisor`, {});
      } catch { /* non-critical — silent */ }
    };
    const interval = setInterval(triggerPhaseAdvisor, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [sessionId]);

  // Second screen broadcast — sends state to popup window via BroadcastChannel
  const { pinContent, sendMindBubble, sendCinematicCard, sendCinematicContribution, sendTaskUpdate, sendRequirementsFocus } = useSecondScreenBroadcast(sessionId, {
    sessionName: sessionInfo.customerName || 'Workshop Session',
    backgroundInfo: backgroundInfo as any,
    currentPhaseIndex,
    phases,
    transcript,
    pendingContributions,
    sessionAvatars,
    speakingAvatar,
    isAvatarsThinking,
    requirements,
    facilitatorGuidance,
    sessionDevices,
    attendees,
    facilitators,
    sessionStatus,
    activeTasks: sessionTasksForScreen,
    governanceData,
    sessionCreatedAt: sessionInfo.createdAt || null,
    inspirationCards,
    graphData,
    pinnedContent,
    requirementsFocus: null,
    doubleDiamondProgress,
  });

  // Avatar mind bubbles — ambient thoughts on timer + phase transitions
  useAvatarMindBubbles(
    sessionId,
    sessionAvatars,
    currentPhaseIndex,
    sessionStatus === 'active',
    avatarBubblesEnabled,
    sendMindBubble,
  );

  // Cinematic feed — polls backend every 5s and broadcasts new cards to second screen
  useCinematicFeed(sessionId, sessionStatus === 'active', sendCinematicCard);

  // Task feed — polls running/completed tasks every 5s and broadcasts to second screen
  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const tasks = await sessionTasksService.listSessionTasks(sessionId, {
          status: ['running', 'pending', 'waiting_human', 'completed', 'failed'],
        });
        setSessionTasksForScreen(tasks);
        sendTaskUpdate(tasks);
      } catch {
        // Silently ignore — not critical
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Double Diamond progress — polls backend every 30s when session is active
  useEffect(() => {
    if (!sessionId || sessionStatus !== 'active') return;
    const poll = async () => {
      try {
        const res = await api.get(`/api/sessions/${sessionId}/dd-progress`);
        if (res.data) setDoubleDiamondProgress({ ...res.data, lastAssessedAt: Date.now() });
      } catch {
        // Non-critical — silently skip
      }
    };
    poll();
    const interval = setInterval(poll, 300_000);
    return () => clearInterval(interval);
  }, [sessionId, sessionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle send message (add to transcript only)
  const handleSend = async () => {
    if (!userInput.trim()) return;

    const message = userInput.trim();
    setUserInput('');

    // Add to transcript
    const humanEntry: TranscriptEntry = {
      speaker: 'human',
      text: message,
      timestamp: new Date().toISOString()
    };
    setTranscript(prev => [...prev, humanEntry]);

    // Persist the transcript entry to the backend
    try {
      await api.post(`/api/sessions/${sessionId}/transcript`, { text: message });
    } catch (err) {
      console.error('[handleSend] Failed to persist transcript entry:', err);
    }
  };

  // Handle auto-respond toggle change (used by DashboardView)
  const handleToggleAutoRespond = async (enabled: boolean) => {
    const previousState = autoRespondEnabled;
    setAutoRespondEnabled(enabled);
    try {
      await api.put(`/api/sessions/${sessionId}/auto-respond`, { autoRespondEnabled: enabled });
    } catch (error) {
      console.error('Failed to update auto-respond state:', error);
      setAutoRespondEnabled(previousState);
      alert('Failed to update auto-respond setting. Please try again.');
    }
  };

  const handleSaveMaxResponseWords = async (value: number) => {
    const previous = maxResponseWords;
    setMaxResponseWords(value);
    try {
      await api.put(`/api/sessions/${sessionId}/max-response-words`, { maxResponseWords: value });
    } catch (error) {
      console.error('Failed to update max response words:', error);
      setMaxResponseWords(previous);
      alert('Failed to save response settings. Please try again.');
    }
  };

  const handleToggleIntentDetect = async (enabled: boolean) => {
    const previous = intentDetectEnabled;
    setIntentDetectEnabled(enabled);
    try {
      await api.patch(`/api/sessions/${sessionId}/intent-detect`, { enabled });
    } catch (error) {
      console.error('Failed to update intent detect setting:', error);
      setIntentDetectEnabled(previous);
    }
  };

  const handleToggleAvatarBubbles = async (enabled: boolean) => {
    const previous = avatarBubblesEnabled;
    setAvatarBubblesEnabled(enabled);
    try {
      await api.patch(`/api/sessions/${sessionId}/avatar-bubbles`, { enabled });
    } catch (error) {
      console.error('Failed to update avatar bubbles setting:', error);
      setAvatarBubblesEnabled(previous);
    }
  };

  const handleSetTtsProvider = async (value: 'off' | 'elevenlabs') => {
    const previous = ttsProvider;
    setTtsProvider(value);
    try {
      await api.patch(`/api/sessions/${sessionId}/tts-provider`, { ttsProvider: value });
    } catch (error) {
      console.error('Failed to update TTS provider:', error);
      setTtsProvider(previous);
    }
  };

  const handleSetConverseProvider = async (value: 'off' | 'elevenlabs') => {
    const previous = converseProvider;
    setConverseProvider(value);
    try {
      await api.patch(`/api/sessions/${sessionId}/converse-provider`, { converseProvider: value });
    } catch (error) {
      console.error('Failed to update converse provider:', error);
      setConverseProvider(previous);
    }
  };

  // Handle send to avatars (add to transcript if text present, then request avatar responses)
  const handleSendToAvatars = async (avatarRoles?: string[]) => {
    const message = userInput.trim();

    // If there's text, add it to the transcript first
    if (message) {
      setUserInput('');
      const humanEntry: TranscriptEntry = {
        speaker: 'human',
        text: message,
        timestamp: new Date().toISOString()
      };
      setTranscript(prev => [...prev, humanEntry]);
    }

    try {
      // Fire-and-forget: returns immediately with requestId
      const { requestId } = await callN8nWorkflow(
        sessionId,
        currentPhase.name,
        currentPhase.objectives,
        responseMode,
        message || undefined,
        projectId,
        avatarRoles  // undefined = all avatars, [...] = specific avatars
      );
      setInFlightRequests(prev => [...prev, { requestId, startedAt: new Date().toISOString() }]);
    } catch (error: any) {
      console.error('Failed to send to avatars:', error);

      if (error.response?.status === 400) {
        alert(error.response?.data?.message || 'Cannot request responses at this time.');
      } else {
        alert('Failed to request avatar responses. Please try again.');
      }
    }
  };

  // Handle manual avatar response request (used by avatar cards panel and contributions tab)
  const handleManualAvatarRequest = async (avatarRoles?: string[]) => {
    try {
      const { requestId } = await callN8nWorkflow(
        sessionId,
        currentPhase.name,
        currentPhase.objectives,
        responseMode,
        undefined,
        projectId,
        avatarRoles
      );
      setInFlightRequests(prev => [...prev, { requestId, startedAt: new Date().toISOString() }]);
    } catch (error: any) {
      console.error('Manual response request failed:', error);
      if (error.response?.status === 400) {
        alert(error.response?.data?.message || 'Cannot request responses at this time.');
      } else {
        alert('Failed to request avatar responses. Please try again.');
      }
    }
  };

  // Handle continuous speech send (from queued sentences)
  const handleContinuousSend = async (sentences: FinalizedSentence[]) => {
    if (sentences.length === 0) return;

    // Concatenate sentences with spacing
    const combinedText = sentences
      .map(s => s.text.trim())
      .filter(s => s.length > 0)
      .join(' ');

    if (!combinedText) return;

    // Deduplication: Check if this is a duplicate send within 2 seconds
    const now = Date.now();
    if (combinedText === lastSentTextRef.current && now - lastSentTimeRef.current < 2000) {
      console.warn(`[Continuous Speech] DUPLICATE DETECTED - Ignoring duplicate send: "${combinedText}"`);
      return;
    }


    // Track this send to prevent duplicates
    lastSentTextRef.current = combinedText;
    lastSentTimeRef.current = now;

    // Add to transcript as single human entry immediately (before any async work)
    const humanEntry: TranscriptEntry = {
      speaker: 'human',
      text: combinedText,
      timestamp: new Date().toISOString()
    };
    setTranscript(prev => [...prev, humanEntry]);

    // Persist transcript entry to backend (continuous speech only adds to transcript, not to avatars)
    try {
      await api.post(`/api/sessions/${sessionId}/transcript`, { text: combinedText });
    } catch (err) {
      console.error('[Continuous Speech] Failed to persist transcript entry:', err);
    }
  };

  // Save pending contributions to backend
  const savePendingContributions = useCallback(async (contributions: AvatarSuggestion[]) => {
    try {
      // Strip transient audio fields — presigned URLs expire and base64 bloats DynamoDB
      const cleaned = contributions.map(({ audioUrl, audioBase64, audioGenerating, ...rest }) => rest);
      await api.put(`/api/sessions/${sessionId}/pending-contributions`, {
        pendingContributions: cleaned
      });
    } catch (error) {
      console.error('[Contributions Persistence] Failed to save pending contributions:', error);
    }
  }, [sessionId]);

  // Persist contributions whenever they change (debounced)
  useEffect(() => {
    if (!hasLoadedContributions.current) {
      return;
    }
    // Keep known count in sync with frontend state (for health check polling comparison)
    knownContributionCount.current = pendingContributions.length;
    const timeoutId = setTimeout(() => {
      if (sessionId) {
        savePendingContributions(pendingContributions);
      }
    }, 1000); // 1 second debounce

    return () => {
      clearTimeout(timeoutId);
    };
  }, [pendingContributions, sessionId, savePendingContributions]);

  // Handle text-only accept (no TTS audio generation)
  const handleApproveTextOnly = async (suggestion: AvatarSuggestion) => {
    try {
      // Add transcript entry (no audio)
      await approveAvatarContributionViaN8n(
        sessionId,
        suggestion.role,
        suggestion.response,
        '' // no audio
      );

      // Add to transcript
      const avatarEntry: TranscriptEntry = {
        speaker: 'avatar',
        avatarRole: suggestion.role,
        text: suggestion.response,
        timestamp: new Date().toISOString()
      };
      setTranscript(prev => [...prev, avatarEntry]);

      // Mark as accepted
      setPendingContributions(prev =>
        prev.map(c => c === suggestion ? { ...c, state: 'accepted' as const } : c)
      );

      // Clean up staged audio now that contribution is consumed
      clearStagedAudio(sessionId, suggestion.role).catch(err =>
        console.error(`[approve text-only] Failed to clear staged audio for ${suggestion.role}:`, err)
      );

      // Relay text to meeting bot chat if configured
      if (meetingConnection && meetingChatEnabled && meetingRelayAvatars.has(suggestion.role)) {
        const avatarName = sessionAvatars.find(a => a.role === suggestion.role)?.name || suggestion.role;
        const chatMessage = `[${avatarName}] ${suggestion.response}`;
        try {
          await botChat(meetingConnection.platform, meetingConnection.meetingId, chatMessage);
        } catch (err) {
          console.error('[approve text-only] Failed to relay to meeting chat:', err);
        }
      }
    } catch (error) {
      console.error('Failed to accept contribution (text-only):', error);
      alert('Failed to accept contribution. Check console.');
    }
  };

  // Accept contribution and push to cinematic second screen
  const handlePushToCinematic = async (suggestion: AvatarSuggestion) => {
    await handleApproveTextOnly(suggestion);
    const avatarInfo = sessionAvatars.find(a => a.role === suggestion.role);
    const strippedText = suggestion.response.replace(/\[[^\]]+\]/g, '').replace(/\s+/g, ' ').trim();
    const contribution: CinematicContribution = {
      id: `${suggestion.role}-${Date.now()}`,
      avatarRole: suggestion.role,
      avatarName: avatarInfo?.name || suggestion.name || suggestion.role,
      text: strippedText,
      timestamp: Date.now(),
    };
    sendCinematicContribution(contribution);
  };

  // Handle approve avatar with audio
  const handleApprove = async (suggestion: AvatarSuggestion) => {
    try {
      // Find the index of this contribution
      const contributionIndex = pendingContributions.findIndex(c => c === suggestion);

      // Mark as processing
      if (contributionIndex !== -1) {
        setProcessingAudio(prev => new Set(prev).add(contributionIndex));
      }

      // Generate TTS audio on-demand (only when user accepts)
      let audioData = suggestion.audioUrl || suggestion.audioBase64 || '';
      let capturedS3Key = suggestion.audioS3Key || '';
      const audioAlreadyStaged = !!audioData;

      // If no audio exists, generate it async via callback and poll for readiness
      if (!audioData) {
        await stageAudioAsync(sessionId, suggestion.role, suggestion.response);

        // Poll for readiness at 2s intervals (user is actively waiting)
        const maxWaitMs = 45000;
        const pollIntervalMs = 2000;
        const startTime = Date.now();

        while (Date.now() - startTime < maxWaitMs) {
          await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
          const stagedAudio = await getStagedAudio(sessionId);
          const entry = stagedAudio[suggestion.role];
          if (entry?.status === 'ready' && (entry.audioUrl || entry.audioBase64)) {
            audioData = (entry.audioUrl || entry.audioBase64)!;
            if (entry.s3Key) capturedS3Key = entry.s3Key;
            // Clean up the staged entry
            clearStagedAudio(sessionId, suggestion.role).catch(() => {});
            break;
          }
          if (entry?.status === 'failed') {
            throw new Error(`TTS generation failed for ${suggestion.role}: ${entry.error}`);
          }
        }

        if (!audioData) {
          throw new Error(`TTS generation timed out for ${suggestion.role} after ${maxWaitMs}ms`);
        }
      }

      // Check if this avatar is set to relay exclusively through a meeting
      const isRelayedToMeeting = meetingConnection && meetingRelayAvatars.has(suggestion.role);

      let releasedToDevice = false;

      if (!isRelayedToMeeting) {
        // Normal flow: queue to device, release, play in browser
        // If audio wasn't pre-staged, queue it now (inserts as 'staged')
        if (!audioAlreadyStaged && audioData) {
          await approveAvatarContributionViaN8n(
            sessionId,
            suggestion.role,
            suggestion.response,
            audioData
          );
        }

        // Release staged → pending so the device picks it up
        const releaseResult = await releaseSpeechViaN8n(sessionId, suggestion.role);
        releasedToDevice = releaseResult.released > 0;

        // Add transcript entry via approve route (without re-queuing audio)
        await approveAvatarContributionViaN8n(
          sessionId,
          suggestion.role,
          suggestion.response,
          '' // empty audio — already staged/released
        );

        // Play audio in browser — skip if released to a physical device
        const audioUrl = audioData;
        const shouldPlayInBrowser = !releasedToDevice && (playbackMode === 'browser' || playbackMode === 'both');
        if (shouldPlayInBrowser && audioUrl) {
          // Stop any existing audio before starting new playback
          if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
            currentAudioRef.current = null;
          }

          const audio = new Audio(audioUrl);
          currentAudioRef.current = audio;

          // Track speaking state to mute speech recognition
          audio.addEventListener('play', () => {
            setSpeakingAvatar(suggestion.role);
          });

          audio.addEventListener('ended', () => {
            setSpeakingAvatar(null);
            currentAudioRef.current = null;
          });

          audio.addEventListener('pause', () => {
            // Only clear if not temporarily paused for buffering
            if (!audio.ended && !audio.seeking) {
              setSpeakingAvatar(null);
            }
          });

          audio.addEventListener('error', (e) => {
            console.error(`[Avatar Speech] Audio playback failed for ${suggestion.role}:`, e);
            setSpeakingAvatar(null); // Critical: Unblock STT
            currentAudioRef.current = null;
          });

          audio.play().catch((err) => {
            console.error(`[Avatar Speech] Failed to play audio for ${suggestion.role}:`, err);
            setSpeakingAvatar(null); // Unblock STT on play failure
          });
        }
      } else {
        // Still add transcript entry
        await approveAvatarContributionViaN8n(
          sessionId,
          suggestion.role,
          suggestion.response,
          '' // no audio needed for meeting relay
        );

        // Send audio to meeting bot — user explicitly chose "Accept & Speak"
        if (audioData) {
          try {
            if (audioData.startsWith('http')) {
              await botSpeak(meetingConnection.platform, meetingConnection.meetingId, {
                audioUrl: audioData,
                format: 'mp3',
              });
            } else {
              const rawBase64 = audioData.replace(/^data:audio\/[^;]+;base64,/, '');
              await botSpeak(meetingConnection.platform, meetingConnection.meetingId, {
                audioBase64: rawBase64,
                format: 'mp3',
              });
            }
          } catch (err) {
            console.error('[vexa relay] Failed to send audio to meeting bot:', err);
          }
        } else {
          console.warn(`[vexa relay] No audioData available for ${suggestion.role} — botSpeak skipped`);
        }
      }

      // Add to transcript (without audioUrl to keep it clean)
      const avatarEntry: TranscriptEntry = {
        speaker: 'avatar',
        avatarRole: suggestion.role,
        text: suggestion.response,
        timestamp: new Date().toISOString()
      };
      setTranscript(prev => [...prev, avatarEntry]);

      // Update contribution state to 'accepted', preserving S3 key for replay
      setPendingContributions(prev =>
        prev.map(c => c === suggestion ? {
          ...c,
          state: 'accepted' as const,
          ...(capturedS3Key ? { audioS3Key: capturedS3Key } : {}),
        } : c)
      );

      // Clean up staged audio now that contribution is consumed
      clearStagedAudio(sessionId, suggestion.role).catch(err =>
        console.error(`[approve] Failed to clear staged audio for ${suggestion.role}:`, err)
      );

      // Relay to meeting bot if this avatar is set to relay through the meeting
      const shouldRelayToMeeting = meetingConnection && meetingRelayAvatars.has(suggestion.role);
      if (shouldRelayToMeeting) {
        // Send text to meeting chat if chat is enabled
        if (meetingChatEnabled) {
          const avatarName = sessionAvatars.find(a => a.role === suggestion.role)?.name || suggestion.role;
          const chatMessage = `[${avatarName}] ${suggestion.response}`;
          try {
            await botChat(meetingConnection.platform, meetingConnection.meetingId, chatMessage);
          } catch (err) {
            console.error('[vexa relay] Failed to relay contribution to meeting chat:', err);
          }
        }

        // Send audio to meeting bot speaker if speak is enabled
        if (meetingSpeakEnabled && audioData) {
          try {
            if (audioData.startsWith('http')) {
              await botSpeak(meetingConnection.platform, meetingConnection.meetingId, {
                audioUrl: audioData,
                format: 'mp3',
              });
            } else {
              const rawBase64 = audioData.replace(/^data:audio\/[^;]+;base64,/, '');
              await botSpeak(meetingConnection.platform, meetingConnection.meetingId, {
                audioBase64: rawBase64,
                format: 'mp3',
              });
            }
          } catch (err) {
            console.error('[vexa relay] Failed to send audio to meeting bot:', err);
          }
        }
      }

      // Remove from processing set
      if (contributionIndex !== -1) {
        setProcessingAudio(prev => {
          const newSet = new Set(prev);
          newSet.delete(contributionIndex);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to approve avatar:', error);
      alert('Failed to approve avatar. Check console.');

      // Remove from processing set on error
      const contributionIndex = pendingContributions.findIndex(c => c === suggestion);
      if (contributionIndex !== -1) {
        setProcessingAudio(prev => {
          const newSet = new Set(prev);
          newSet.delete(contributionIndex);
          return newSet;
        });
      }
    }
  };

  const handleStageAudio = async (suggestion: AvatarSuggestion, index: number) => {
    try {
      // If audio already exists or is generating, do nothing
      if (suggestion.audioUrl || suggestion.audioBase64 || suggestion.audioGenerating) {
        return;
      }

      // Mark as processing
      setProcessingAudio(prev => new Set(prev).add(index));


      // Fire-and-forget: returns immediately with requestId
      await stageAudioAsync(sessionId, suggestion.role, suggestion.response);

      // Mark contribution as "generating audio" (visual feedback)
      // processingAudio stays set — will be cleared when health check detects ready audio
      setPendingContributions(prev =>
        prev.map((c, i) => i === index ? { ...c, audioGenerating: true } : c)
      );
    } catch (error) {
      console.error('Failed to initiate audio staging:', error);
      alert('Failed to initiate audio staging. Check console.');

      // Remove from processing set on error
      setProcessingAudio(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleSaveBackground = async (data: BackgroundInfo) => {
    setBackgroundInfo(data);
    try {
      await api.patch(`/api/sessions/${sessionId}/background-info`, { backgroundInfo: data });
    } catch (err) {
      console.error('[Background] Failed to persist background info:', err);
    }
  };

  const handleSaveGoals = async (goals: string[]) => {
    setSessionGoals(goals);
    try {
      await api.patch(`/api/sessions/${sessionId}/goals`, { goals });
    } catch (err) {
      console.error('[Goals] Failed to persist goals:', err);
    }
  };

  const handleSaveDesignContext = (context: string) => {
    setDesignContext(context);
  };

  const handleSaveImageStyle = (style: string) => {
    setImageStyle(style);
  };

  const handleGenerateImage = async (params: { prompt: string; promptType: string; aspectRatio: string }): Promise<string> => {
    const { generateSessionImage } = await import('../services/imageGen');
    const result = await generateSessionImage(sessionId, params as any);
    return result.imageId;
  };

  const handleAddAttendee = (attendee: Omit<Attendee, 'id'>) => {
    const newAttendee: Attendee = {
      ...attendee,
      id: `attendee_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    setAttendees(prev => [...prev, newAttendee]);
  };

  const handleRemoveAttendee = async (id: string) => {
    setAttendees(prev => prev.filter(a => a.id !== id));
    if (projectId && window.confirm('Also remove this attendee from the knowledge graph?')) {
      try {
        await customerResearchService.syncParticipants(projectId, sessionId, attendees.filter(a => a.id !== id), facilitators, [id]);
        setTimeout(async () => {
          try {
            const data = await customerResearchService.getGraphData(projectId);
            setGraphData(data);
          } catch { /* silent */ }
        }, 3000);
      } catch { /* silent */ }
    }
  };

  const handleAddFacilitator = (facilitator: Omit<Attendee, 'id'>) => {
    const newFacilitator: Attendee = {
      ...facilitator,
      id: `facilitator_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    setFacilitators(prev => [...prev, newFacilitator]);
  };

  const handleRemoveFacilitator = async (id: string) => {
    setFacilitators(prev => prev.filter(f => f.id !== id));
    if (projectId && window.confirm('Also remove this facilitator from the knowledge graph?')) {
      try {
        await customerResearchService.syncParticipants(projectId, sessionId, attendees, facilitators.filter(f => f.id !== id), [id]);
        setTimeout(async () => {
          try {
            const data = await customerResearchService.getGraphData(projectId);
            setGraphData(data);
          } catch { /* silent */ }
        }, 3000);
      } catch { /* silent */ }
    }
  };

  const handleAddGuidance = (guidance: Omit<FacilitatorGuidance, 'id' | 'timestamp'>) => {
    const newGuidance: FacilitatorGuidance = {
      ...guidance,
      id: `guidance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };
    setGuidanceHistory(prev => [...prev, newGuidance]);
  };

  const handleWhiteboardUploadSuccess = (result: {
    extractedInfo: string;
    sourceId: string;
    sourceName: string;
    timestamp: string;
    isNewInfo: boolean;
  }) => {
    // Add to transcript as whiteboard entry
    const whiteboardEntry: TranscriptEntry = {
      speaker: 'whiteboard',
      text: result.extractedInfo,
      timestamp: result.timestamp,
      sourceId: result.sourceId,
      sourceName: result.sourceName
    };
    setTranscript(prev => [...prev, whiteboardEntry]);

    // Reload sources
    loadWhiteboardSources();

  };

  const handleSourceCleared = (sourceId: string) => {
    setWhiteboardSources(prev => prev.filter(s => s.sourceId !== sourceId));
  };

  const loadWhiteboardSources = useCallback(async () => {
    if (!sessionId) return;
    try {
      const sources = await getWhiteboardSources(sessionId);
      setWhiteboardSources(sources);
    } catch (error) {
      console.error('Failed to load whiteboard sources:', error);
      toast.error('Failed to load whiteboard sources');
    }
  }, [sessionId]);

  // Load whiteboard sources when modal opens
  useEffect(() => {
    if (showWhiteboardModal && sessionId) {
      loadWhiteboardSources();
    }
  }, [showWhiteboardModal, sessionId, loadWhiteboardSources]);

  const handleSkipSuggestion = (index: number) => {
    // Clean up staged audio for dismissed contribution
    const contribution = pendingContributions[index];
    if (contribution) {
      clearStagedAudio(sessionId, contribution.role).catch(() => {});
    }
    // Update contribution state to 'ignored' (passive dismiss — no strong signal)
    setPendingContributions(prev =>
      prev.map((c, i) => i === index ? { ...c, state: 'ignored' as const } : c)
    );
    // Fire-and-forget signal to backend
    if (contribution?.contributionId && sessionId) {
      api.post(`/api/sessions/${sessionId}/contributions/${contribution.contributionId}/feedback`, { feedback: 'ignored' }).catch(() => {});
    }
  };

  const handleRejectSuggestion = (index: number, reason?: string) => {
    const contribution = pendingContributions[index];
    if (contribution) {
      clearStagedAudio(sessionId, contribution.role).catch(() => {});
    }
    // Update contribution state to 'rejected' (explicit facilitator dismissal)
    setPendingContributions(prev =>
      prev.map((c, i) => i === index ? { ...c, state: 'rejected' as const } : c)
    );
    if (contribution?.contributionId && sessionId) {
      api.post(`/api/sessions/${sessionId}/contributions/${contribution.contributionId}/feedback`, {
        feedback: 'rejected',
        ...(reason ? { reason } : {})
      }).catch(() => {});
    }
  };

  const handleEditContribution = (index: number, newText: string) => {
    // Clear staged audio (text changed, TTS no longer matches) and governor reason
    const contribution = pendingContributions[index];
    if (contribution) {
      clearStagedAudio(sessionId, contribution.role).catch(() => {});
    }
    setPendingContributions(prev =>
      prev.map((c, i) => i === index ? {
        ...c,
        response: newText,
        preview: newText.slice(0, 120),
        audioUrl: undefined,
        audioBase64: undefined,
        audioGenerating: false,
        governorReason: '',
      } : c)
    );
  };

  const handleCreateContribution = (role: string, text: string) => {
    const avatar = sessionAvatars.find(a => a.role === role);
    const newContribution: AvatarSuggestion = {
      role,
      name: avatar?.name || role,
      response: text,
      preview: text.slice(0, 120),
      urgency: 'normal',
      governorReason: '',
      governorPriority: 0,
      state: 'pending',
      createdAt: new Date().toISOString(),
    };
    setPendingContributions(prev => [newContribution, ...prev]);
  };

  const handleReplayAudio = async (suggestion: AvatarSuggestion) => {
    if (!suggestion.audioS3Key) return;
    try {
      const res = await api.post('/api/s3/presigned-url', {
        bucket: 'dev-spectrum-project-data',
        key: suggestion.audioS3Key,
        expiresIn: 300,
      });
      if (res.data?.url) {
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        }

        const audio = new Audio(res.data.url);
        currentAudioRef.current = audio;
        audio.addEventListener('play', () => setSpeakingAvatar(suggestion.role));
        audio.addEventListener('ended', () => { setSpeakingAvatar(null); currentAudioRef.current = null; });
        audio.addEventListener('error', () => { setSpeakingAvatar(null); currentAudioRef.current = null; });
        audio.play().catch(() => setSpeakingAvatar(null));
      }
    } catch (err) {
      console.error('[Replay] Failed to get presigned URL:', err);
    }
  };

  const handleStopAudio = useCallback(async () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setSpeakingAvatar(null);
    try {
      await stopSpeechViaN8n(sessionId);
    } catch (err) {
      console.error('[Stop Speech] Failed to stop device audio:', err);
    }
  }, [sessionId]);

  const handleCopyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    toast.success('Copied to clipboard');
  };

  const handleRemoveRequirement = (requirementId: string) => {
    setRemovedRequirementIds(prev => {
      const updated = new Set([...prev, requirementId]);
      try {
        localStorage.setItem(`removedRequirements_${sessionId}`, JSON.stringify([...updated]));
      } catch (error) {
        console.error('Failed to save removed requirements to localStorage:', error);
      }
      api.patch(`/api/sessions/${sessionId}/removed-items`, { removedStoryIds: [...updated] }).catch(() => {});
      return updated;
    });
  };

  const handleRemovePersona = (personaId: string) => {
    setRemovedPersonaIds(prev => {
      const updated = new Set([...prev, personaId]);
      try {
        localStorage.setItem(`removedPersonas_${sessionId}`, JSON.stringify([...updated]));
      } catch (error) {
        console.error('Failed to save removed personas to localStorage:', error);
      }
      api.patch(`/api/sessions/${sessionId}/removed-items`, { removedPersonaIds: [...updated] }).catch(() => {});
      return updated;
    });
  };

  // Import transcript from JSON file
  const handleImportTranscript = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = JSON.parse(event.target?.result as string);

          if (!data.transcript || !Array.isArray(data.transcript)) {
            alert('Invalid transcript file format');
            return;
          }

          if (transcript.length > 0) {
            const confirm = window.confirm(
              'This will replace the current transcript. Are you sure you want to continue?'
            );
            if (!confirm) return;
          }

          // Update frontend state
          setTranscript(data.transcript);

          // Send to backend
          try {
            const response = await api.post(`/api/sessions/${sessionId}/transcript/import`, {
              transcript: data.transcript
            });

          } catch (backendError) {
            console.error('Failed to sync transcript with backend:', backendError);
            alert('Transcript imported to UI, but failed to sync with backend. Requirements extraction may not work correctly.');
          }
        } catch (error) {
          console.error('Failed to import transcript:', error);
          alert('Failed to import transcript. Please check the file format.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Delete specific transcript entries
  const handleDeleteTranscriptEntries = async (entries: { id: string; timestamp: string }[]) => {
    await api.delete(`/api/sessions/${sessionId}/transcript/bulk`, { data: { entries } });
    const deletedIds = new Set(entries.map(e => e.id));
    setTranscript(prev => prev.filter(t => !t.id || !deletedIds.has(t.id)));
  };

  const handleAttributeEntry = async (entryId: string, timestamp: string, attributedTo: Array<{ id: string; name: string; role: string; organization: string }>) => {
    const entryText = transcript.find(t => t.id === entryId)?.text;
    await sessionService.attributeTranscriptEntry(sessionId, entryId, timestamp, attributedTo, projectId, entryText);
    const attributedToIds = attributedTo.map(p => p.id);
    setTranscript(prev => prev.map(t => t.id === entryId ? { ...t, attributedTo: attributedToIds } : t));
    if (projectId) {
      new Promise(r => setTimeout(r, 2000))
        .then(() => customerResearchService.getGraphData(projectId!))
        .then(setGraphData)
        .catch(() => {});
    }
  };

  // Delete specific knowledge graph nodes
  const handleDeleteGraphNodes = async (nodeIds: string[]) => {
    if (!projectId) return;
    await customerResearchService.deleteGraphNodes(projectId, nodeIds);
    const updated = await customerResearchService.getGraphData(projectId);
    setGraphData(updated);
  };

  // Governance handlers
  const handleSyncCredo = async () => {
    if (!sessionId) return;

    setGovernanceLoading(true);
    try {
      await sessionTasksService.createTask(sessionId, {
        taskTemplateId: 'session-task-credo-governance-sync',
        taskName: 'Credo Governance Sync',
        taskCategory: 'doing',
        contextSnapshot: {
          customContext: {
            customerName: backgroundInfo.customerName || 'Workshop Customer',
            sessionId,
            sessionName: backgroundInfo.customerName || 'Workshop Session',
            projectId: projectId || ''
          }
        }
      });
    } catch (error) {
      console.error('Failed to trigger Credo governance sync:', error);
      alert('Failed to sync with Credo.ai. Check console for details.');
    } finally {
      setGovernanceLoading(false);
    }
  };

  const toggleSurvey = (surveyId: string) => {
    setExpandedSurveys(prev => {
      const updated = new Set(prev);
      if (updated.has(surveyId)) {
        updated.delete(surveyId);
      } else {
        updated.add(surveyId);
      }
      return updated;
    });
  };

  // Get avatar contributions for the cards
  const getAvatarContributions = (): Record<string, number> => {
    const contributions: Record<string, number> = {};
    transcript.forEach(entry => {
      if (entry.speaker === 'avatar' && entry.avatarRole) {
        const role = normalizeRole(entry.avatarRole);
        contributions[role] = (contributions[role] || 0) + 1;
      }
    });
    return contributions;
  };

  const getPendingAvatarRoles = (): string[] => {
    return pendingContributions
      .filter(c => c.state !== 'accepted' && c.state !== 'ignored')
      .map(c => normalizeRole(c.role));
  };

  // Continuous speech recognition
  const {
    isListening: isListeningContinuous,
    isSupported,
    interimTranscript,
    start: startContinuous,
    stop: stopContinuous,
  } = useContinuousSpeechRecognition({
    disabled: speakingAvatar !== null,
    onTopicComplete: (sentences) => {
      handleContinuousSend(sentences);
    },
    onError: (error) => {
      console.error('Continuous speech recognition error:', error);
      if (error.includes('permission') || error.includes('microphone found')) {
        alert(error);
      }
    },
    sentencePauseMs: 1500,
    topicPauseMs: 4500
  });

  // Helper to render the content for the active view
  const renderViewContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <DashboardView
            currentPhase={currentPhase}
            responseMode={responseMode}
            onResponseModeChange={setResponseMode}
            autoRespondEnabled={autoRespondEnabled}
            onToggleAutoRespond={handleToggleAutoRespond}
            loadingAutoRespondState={loadingAutoRespondState}
            sessionMode={sessionMode}
            userAccessLevel={userAccessLevel}
            userInput={userInput}
            onUserInputChange={setUserInput}
            onSend={handleSend}
            isLoading={isAvatarsThinking}
            isListeningContinuous={isListeningContinuous}
            isSupported={isSupported}
            onStartContinuous={startContinuous}
            onStopContinuous={stopContinuous}
            transcript={transcript}
            pendingContributions={pendingContributions}
            sessionAvatars={sessionAvatars}
            speakingAvatar={speakingAvatar}
            facilitatorGuidance={facilitatorGuidance}
            guidanceLoading={guidanceLoading}
            onRefreshGuidance={loadFacilitatorGuidance}
            onCopyPrompt={handleCopyPrompt}
            onApprove={handleApprove}
            onIgnore={handleSkipSuggestion}
            onReject={handleRejectSuggestion}
            onNavigateTo={(view) => setActiveView(view as SidebarView)}
          />
        );

      case 'transcript':
        return (
          <TranscriptTab
            transcript={transcript}
            avatars={sessionAvatars.map(a => ({ role: a.role, name: a.name, emoji: a.emoji, imageUrl: a.imageUrl, primaryColor: a.primaryColor, secondaryColor: a.secondaryColor }))}
            onImport={handleImportTranscript}
            userInput={userInput}
            onUserInputChange={setUserInput}
            onSend={handleSend}
            onSendToAvatars={handleSendToAvatars}
            isLoading={isAvatarsThinking}
            responseMode={responseMode}
            onResponseModeChange={setResponseMode}
            isListeningContinuous={isListeningContinuous}
            isSupported={isSupported}
            onStartContinuous={startContinuous}
            onStopContinuous={stopContinuous}
            interimTranscript={interimTranscript}
            sessionMode={sessionMode}
            userAccessLevel={userAccessLevel}
            avatarRequestLoading={isAvatarsThinking}
            onDeleteEntries={handleDeleteTranscriptEntries}
            graphData={graphData}
            onDeleteGraphNodes={handleDeleteGraphNodes}
            sessionPeople={[...attendees, ...facilitators]}
            onAttributeEntry={handleAttributeEntry}
          />
        );

      case 'avatars':
        return (
          <AvatarCardsPanel
            sessionId={sessionId}
            avatarContributions={getAvatarContributions()}
            pendingAvatars={getPendingAvatarRoles()}
            speakingAvatar={speakingAvatar}
            sessionAvatars={sessionAvatars}
            responseMode={responseMode}
            onResponseModeChange={setResponseMode}
            onRequestAvatar={(role) => handleManualAvatarRequest([role])}
            loading={isAvatarsThinking}
            projectId={projectId}
            onAvatarCreated={(avatar) => {
              setSessionAvatars(prev => [...prev, {
                id: avatar.avatarDefinitionId,
                name: avatar.name,
                role: avatar.avatarDefinitionId.replace(/[^a-z0-9]+/gi, '_').toLowerCase(),
                state: 'IDLE',
                contributions: 0,
                avatarDefinitionId: avatar.avatarDefinitionId,
                imageUrl: avatar.imageUrl,
                primaryColor: avatar.primaryColor,
                secondaryColor: avatar.secondaryColor
              }]);
            }}
          />
        );

      case 'contributions':
        return (
          <AvatarContributionsTab
            contributions={pendingContributions}
            avatars={sessionAvatars.map(a => ({ role: a.role, name: a.name, imageUrl: a.imageUrl, primaryColor: a.primaryColor, secondaryColor: a.secondaryColor }))}
            onAccept={handleApproveTextOnly}
            onAcceptWithAudio={handleApprove}
            onIgnore={handleSkipSuggestion}
            onReject={handleRejectSuggestion}
            onStageAudio={handleStageAudio}
            onRequestAll={() => handleManualAvatarRequest()}
            onRequestSelected={(roles) => handleManualAvatarRequest(roles)}
            onPushToCinematic={handlePushToCinematic}
            onEditContribution={handleEditContribution}
            onCreateContribution={handleCreateContribution}
            onReplayAudio={handleReplayAudio}
            speakingAvatar={speakingAvatar}
            onStopAudio={handleStopAudio}
            loading={isAvatarsThinking}
            hasTranscript={transcript.length > 0}
            processingAudio={processingAudio}
            isSecondScreenOpen={isSecondScreenOpen}
            ttsProvider={ttsProvider}
          />
        );

      case 'guidance':
        return (
          <FacilitatorGuidancePanel
            guidance={facilitatorGuidance}
            loading={guidanceLoading}
            onRefresh={loadFacilitatorGuidance}
            onCopyPrompt={handleCopyPrompt}
            currentPhase={currentPhase}
            sessionId={sessionId}
            transcript={transcript}
            sessionAvatars={sessionAvatars}
            requirements={requirements}
          />
        );

      case 'requirements':
        return (
          <RequirementsPanel
            requirements={requirements}
            loading={requirementsLoading}
            hasTranscript={transcript.length > 0}
            removedRequirementIds={removedRequirementIds}
            removedPersonaIds={removedPersonaIds}
            onRefresh={loadRequirements}
            onReloadData={reloadRequirementsData}
            onRemoveRequirement={handleRemoveRequirement}
            onRemovePersona={handleRemovePersona}
            onClearAll={async () => {
              const { clearRequirements } = await import('../services/workshop');
              await clearRequirements(sessionId);
              setRequirements(null);
              setRemovedRequirementIds(new Set());
              setRemovedPersonaIds(new Set());
              try {
                localStorage.removeItem(`removedRequirements_${sessionId}`);
                localStorage.removeItem(`removedPersonas_${sessionId}`);
              } catch {}
              api.patch(`/api/sessions/${sessionId}/removed-items`, { removedStoryIds: [], removedPersonaIds: [] }).catch(() => {});
            }}
            projectId={projectId}
            sessionId={sessionId}
            onAvatarCreated={(avatar) => {
              setSessionAvatars(prev => [...prev, {
                id: avatar.avatarDefinitionId,
                name: avatar.name,
                role: avatar.avatarDefinitionId.replace(/[^a-z0-9]+/gi, '_').toLowerCase(),
                state: 'IDLE',
                contributions: 0,
                avatarDefinitionId: avatar.avatarDefinitionId,
                imageUrl: avatar.imageUrl,
                primaryColor: avatar.primaryColor,
                secondaryColor: avatar.secondaryColor
              }]);
            }}
            {...(hasFeature('open_design') && {
              onStartOpenDesignTask: async (prompt: string, skillId: string | null = null) => {
                await sessionTasksService.createTask(sessionId, {
                  taskTemplateId: 'session-task-start-open-design',
                  taskName: `Design: ${prompt.substring(0, 50)}${prompt.length > 50 ? '…' : ''}`,
                  taskCategory: 'doing',
                  contextSnapshot: {
                    customContext: {
                      prompt,
                      autoGeneratePrompt: false,
                      projectId: projectId || '',
                      ...(skillId ? { skillId } : {}),
                      ...(designContext ? { designContext } : {})
                    }
                  }
                });
              }
            })}
            onActivePersonaChange={(personaId) => {
              activeRequirementsPersonaRef.current = personaId;
              sendRequirementsFocus(personaId, 0);
            }}
            onStoriesScroll={(scrollTop) => sendRequirementsFocus(activeRequirementsPersonaRef.current, scrollTop)}
            onPushToScreen={handlePushRequirementsToScreen}
            onGenerateImage={handleGenerateImage}
          />
        );

      case 'decisions':
        return (
          <DecisionsTab
            decisions={decisions}
            loading={decisionsLoading}
            scanning={decisionsScanning}
            hasTranscript={transcript.length > 0}
            onAccept={handleAcceptDecision}
            onReject={handleRejectDecision}
            onEditAccept={handleEditAcceptDecision}
            onScan={handleDecisionScan}
          />
        );

      case 'actions':
        return (
          <ActionsPanel
            sessionId={sessionId}
            onPendingCountChange={setPendingActionsCount}
          />
        );

      case 'governance':
        return (
          <GovernancePanel
            data={governanceData}
            loading={governanceLoading}
            hasTranscript={transcript.length > 0}
            isReadOnly={userAccessLevel === 'read'}
            expandedSurveys={expandedSurveys}
            onSync={handleSyncCredo}
            onToggleSurvey={toggleSurvey}
          />
        );

      case 'session-analytics':
        return (
          <SessionAnalyticsPanel sessionId={sessionId} projectId={projectId} />
        );

      case 'session-graph':
        return projectId ? (
          <SessionGraphTab projectId={projectId} sessionId={sessionId} />
        ) : null;

      case 'session-summary':
        return (
          <SessionSummaryPanel
            sessionId={sessionId}
            projectId={projectId}
            sessionName={sessionInfo.customerName || 'Workshop Session'}
            isReadOnly={userAccessLevel === 'read'}
            hasTranscript={transcript.length > 0}
            requirements={requirements}
          />
        );

      case 'report':
        return (
          <ReportBuilderTab
            projectId={projectId || ''}
            sessionId={sessionId}
            scope="session"
            token=""
            onPinToSecondScreen={(sectionId, title, content) => {
              const item: PinnedContentItem = {
                id: `report-${sectionId}-${Date.now()}`,
                type: 'markdown',
                content,
                title: `Report: ${title}`,
                source: 'Report Builder',
                pinnedAt: Date.now(),
              };
              setPinnedContent(prev => [...prev, item]);
              pinContent(item, activeScreenTarget);
            }}
          />
        );

      case 'activity':
        return (
          <div className="flex-1 overflow-y-auto p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Activity</h2>
            <div className="max-w-lg">
              <WidgetLauncherPanel sessionId={sessionId} fullPage />
            </div>
          </div>
        );

      case 'tasks':
        return (
          <SessionTasksTab
            sessionId={sessionId}
            projectId={projectId}
            designContext={designContext}
            sessionContext={{
              transcriptEntries: transcript.map((entry, index) => ({ id: `transcript-${index}-${entry.timestamp}`, timestamp: entry.timestamp, role: entry.speaker === 'avatar' ? (entry.avatarRole || 'avatar') : entry.speaker, content: entry.text, preview: entry.text.substring(0, 150) + (entry.text.length > 150 ? '...' : '') })),
              requirements: (requirements?.userStories || []).filter(s => !removedRequirementIds.has(s.id)).map(s => ({ id: s.id, title: s.story, description: s.acceptanceCriteria?.join('\n') || s.story, type: 'user_story' })),
              personas: (requirements?.personas || []).filter(p => !removedPersonaIds.has(p.id)).map(p => ({ id: p.id, name: p.name, role: p.role || '', description: p.description || '' })),
              sessionAvatars: sessionAvatars.map(a => ({ id: a.role || a.name, name: a.name, role: a.role || '' })),
            }}
            onTaskSelect={(workflowId, executionId) => setSelectedTaskDetail({ workflowId, executionId })}
          />
        );

      case 'images':
        return (
          <ImagesTab
            sessionId={sessionId}
            currentPhase={currentPhase?.id || 'intros'}
            sessionDevices={sessionDevices}
            onPinToSecondScreen={(item) => {
              setPinnedContent(prev => [...prev, item]);
              pinContent(item, activeScreenTarget);
            }}
            sessionAvatars={sessionAvatars}
            onGenerateBook={() => setShowWorkshopBookModal(true)}
            hasWorkshopBookFeature={hasFeature('workshop_book')}
            onGenerateImage={handleGenerateImage}
            {...(hasFeature('open_design') && {
              onStartOpenDesignTask: async (prompt: string, skillId: string | null = null) => {
                const label = prompt.substring(0, 50);
                try {
                  await sessionTasksService.createTask(sessionId, {
                    taskTemplateId: 'session-task-start-open-design',
                    taskName: `Design: ${label}${prompt.length > 50 ? '…' : ''}`,
                    taskCategory: 'doing',
                    contextSnapshot: {
                      customContext: {
                        prompt,
                        autoGeneratePrompt: false,
                        projectId: projectId || '',
                        ...(skillId ? { skillId } : {}),
                        ...(designContext ? { designContext } : {})
                      }
                    }
                  });
                } catch (err) {
                  toast.error(`Failed to start Open Design task: ${err instanceof Error ? err.message : String(err)}`);
                  throw err;
                }
              }
            })}
          />
        );

      case 'devices':
        return <DevicesTab sessionId={sessionId} currentPhase={currentPhase?.id || 'intros'} sessionAvatars={sessionAvatars} roomId={sessionRoomId} roomName={sessionRoomName} roomLeaseExpiresAt={sessionRoomLeaseExpiresAt} converseProvider={converseProvider} />;

      case 'second-screen':
        return (
          <SecondScreenConfigPanel
            sessionId={sessionId}
            isOpen={screen1.isOpen}
            onOpen={openSecondScreen}
            isThirdScreenOpen={screen2.isOpen}
            onOpenThirdScreen={openThirdScreen}
            onActiveScreenChange={setActiveScreenTarget}
          />
        );

      case 'meetings':
        return (
          <MeetingsTab
            sessionId={sessionId}
            sessionAvatars={sessionAvatars}
            meetingConnection={meetingConnection}
            onMeetingConnectionChange={setMeetingConnection}
            meetingChatEnabled={meetingChatEnabled}
            onMeetingChatEnabledChange={setMeetingChatEnabled}
            meetingSpeakEnabled={meetingSpeakEnabled}
            onMeetingSpeakEnabledChange={setMeetingSpeakEnabled}
            meetingRelayAvatars={meetingRelayAvatars}
            onMeetingRelayAvatarsChange={setMeetingRelayAvatars}
          />
        );

      case 'reach-extract':
        return <ReachExtractTab sessionId={sessionId} projectId={projectId} />;

      case 'showcase':
        return (
          <ShowcaseTab
            sessionId={sessionId}
            showcaseEnabled={showcaseEnabled}
            showcaseId={showcaseId}
            showcaseLinks={showcaseLinks}
          />
        );

      case 'miro':
        return <MiroSyncPanel sessionId={sessionId} projectId={projectId} sessionName={sessionInfo.customerName} />;

      case 'setup':
        return (
          <SessionSetupView
            backgroundInfo={backgroundInfo}
            onSaveBackground={handleSaveBackground}
            goals={sessionGoals}
            onSaveGoals={handleSaveGoals}
            designContext={designContext}
            onSaveDesignContext={handleSaveDesignContext}
            imageStyle={imageStyle}
            onSaveImageStyle={handleSaveImageStyle}
            attendees={attendees}
            onAddAttendee={handleAddAttendee}
            onRemoveAttendee={handleRemoveAttendee}
            facilitators={facilitators}
            onAddFacilitator={handleAddFacilitator}
            onRemoveFacilitator={handleRemoveFacilitator}
            maxResponseWords={maxResponseWords}
            onSaveMaxResponseWords={handleSaveMaxResponseWords}
            intentDetectEnabled={intentDetectEnabled}
            onToggleIntentDetect={handleToggleIntentDetect}
            avatarBubblesEnabled={avatarBubblesEnabled}
            onToggleAvatarBubbles={handleToggleAvatarBubbles}
            ttsProvider={ttsProvider}
            onSetTtsProvider={handleSetTtsProvider}
            converseProvider={converseProvider}
            onSetConverseProvider={handleSetConverseProvider}
          />
        );

      case 'export':
        return (
          <ExportPanel
            sessionId={sessionId}
            projectId={projectId}
            sessionName={sessionInfo.customerName}
            requirements={requirements}
            removedPersonaIds={removedPersonaIds}
            removedRequirementIds={removedRequirementIds}
            backgroundInfo={backgroundInfo}
            goals={sessionGoals}
            designContext={designContext}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SessionLayout
        sessionId={sessionId}
        sessionName={sessionInfo.customerName || 'Workshop Session'}
        phases={phases}
        currentPhaseIndex={currentPhaseIndex}
        onPhaseChange={handlePhaseChange}
        isConnected={!sessionNeedsReload}
        playbackMode={playbackMode}
        onPlaybackModeChange={setPlaybackMode}
        onShareSession={() => setShowShareModal(true)}
        onEndSession={async () => {
          if (window.confirm('Are you sure you want to end this session?')) {
            try {
              await endSession(sessionId);
              navigate(projectId ? `/projects/${projectId}` : '/projects');
            } catch (err) {
              console.error('Failed to end session:', err);
              alert('Failed to end session. Please try again.');
            }
          }
        }}
        sessionMode={sessionMode}
        projectId={projectId}
        projectName={projectData?.name}
        pendingContributionsCount={pendingContributions.filter(c => c.state !== 'accepted' && c.state !== 'ignored').length}
        pendingDecisionsCount={decisions.filter(d => d.status === 'pending').length}
        pendingActionsCount={pendingActionsCount}
        avatarCount={sessionAvatars.length}
        activeView={activeView}
        onViewChange={setActiveView}
        hasVexaFeature={hasFeature('vexa')}
        hasMiroFeature={hasFeature('miro')}
        hasReachFeature={hasFeature('reach') && !!projectId}
        sessionStatus={sessionStatus}
        userAccessLevel={userAccessLevel}
        onReopenSession={async () => {
          try {
            await reopenSession(sessionId);
            setSessionMode('active');
            setSessionStatus('active');
          } catch (err) {
            console.error('Failed to reopen session:', err);
            alert('Failed to reopen session. Please try again.');
          }
        }}
        isSecondScreenOpen={isSecondScreenOpen}
        onOpenSecondScreen={openSecondScreen}
        onRename={async (newName: string) => {
          try {
            await api.patch(`/api/sessions/${sessionId}/name`, { customerName: newName });
            setSessionInfo(prev => ({ ...prev, customerName: newName }));
          } catch (err) {
            console.error('Failed to rename session:', err);
          }
        }}
        roomMicMuted={roomMicMuted}
        onToggleRoomMic={handleToggleRoomMic}
        onAuditData={() => setShowAuditModal(true)}
      >
        {renderViewContent()}
      </SessionLayout>
      {/* Modals */}
      <DeletionProgressModal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        onConfirm={() => {}}
        title="Session data audit"
        message=""
        isProcessing={false}
        sessionId={sessionId}
        auditOnly
      />
      <GuidanceModal
        isOpen={showGuidanceModal}
        onClose={() => setShowGuidanceModal(false)}
        onAddGuidance={handleAddGuidance}
        guidanceHistory={guidanceHistory}
      />

      <WhiteboardUploadModal
        isOpen={showWhiteboardModal}
        onClose={() => setShowWhiteboardModal(false)}
        sessionId={sessionId}
        currentPhase={currentPhase.id}
        sources={whiteboardSources}
        onUploadSuccess={handleWhiteboardUploadSuccess}
        onSourceCleared={handleSourceCleared}
      />

      <ShareSessionDialog
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        sessionId={sessionId}
        sessionName={sessionInfo.customerName || 'Workshop Session'}
      />

      {selectedTaskDetail && (
        <SessionTaskDetailModal
          isOpen={true}
          onClose={() => setSelectedTaskDetail(null)}
          sessionId={sessionId}
          workflowId={selectedTaskDetail.workflowId}
          executionId={selectedTaskDetail.executionId}
          onIterateSuccess={(workflowId, executionId) => {
            setSelectedTaskDetail({ workflowId, executionId });
          }}
          onRerun={async (workflowId) => {
            try {
              await sessionTasksService.rerunTask(sessionId, workflowId);
              setSelectedTaskDetail(null);
            } catch (err: any) {
              alert('Failed to rerun task: ' + err.message);
            }
          }}
          onAddToContext={async (workflowId) => {
            try {
              const result = await sessionTasksService.addTaskToContext(sessionId, workflowId);
              alert(`Added ${result.addedCount} outputs to session context`);
            } catch (err: any) {
              alert('Failed to add to context: ' + err.message);
            }
          }}
          onPushToScreen={(item) => {
            const pinned: PinnedContentItem = {
              id: `pin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              ...item,
              pinnedAt: Date.now(),
            };
            setPinnedContent(prev => [...prev, pinned]);
            pinContent(pinned, activeScreenTarget);
          }}
        />
      )}

      <WorkshopBookModal
        isOpen={showWorkshopBookModal}
        onClose={() => setShowWorkshopBookModal(false)}
        sessionId={sessionId}
        personas={requirements?.personas?.filter(p => !removedPersonaIds.has(p.id)) || []}
        onTaskCreated={(workflowId, executionId) => {
          setSelectedTaskDetail({ workflowId, executionId });
        }}
      />
    </>
  );
}
